import { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo, memo } from 'react';
import { txApi } from '../txApi.js';
import { C, btn, label } from './_shared.jsx';
import { SERIES, T } from '../../../shared/styles/tokens.js';

export const mono = { fontFamily: C.mono };
export const uid  = () => Math.random().toString(36).slice(2, 9);

/** The allowed values for a dropdown field, or null when the field is free text (or the
 *  dictionary has not loaded yet and nothing can honestly be judged against it). */
function fieldOptions(f, dictValues) {
  if (f.field_type !== 'dropdown') return null;
  const vals = f.dict_list_id
    ? (dictValues?.[f.dict_list_id] || []).map(v => v.value)
    : (f.dictionary || []).map(d => d.value);
  return vals.length ? vals : null;
}

/** Values arriving from outside the grid (paste, import) are coerced to what the column is. */
export function coerceForField(f, raw) {
  // The raw value is passed through, not its string form: an import hands over real Date
  // objects, and stringifying one first would throw away the only unambiguous reading of it.
  if (f.field_type === 'date') {
    const iso = normaliseDate(raw);
    if (iso) return iso;
  }
  return cellText(raw).trim();   // left as-is when it is not a date, so validation can say so
}

/** How a raw sheet value reads as text — Dates as ISO, everything else as itself. */
export function cellText(raw) {
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) return normaliseDate(raw) ?? '';
  return String(raw ?? '');
}

// Returns an error string or null
function validateField(f, val, options = null) {
  const v = (val ?? '').toString().trim();
  if (f.is_required && !v) return `${f.name} is required`;
  if (v && f.field_type === 'date' && !normaliseDate(v)) return `${f.name}: "${v}" is not a date`;
  if (v && f.max_length && v.length > f.max_length) return `Max ${f.max_length} characters`;
  if (v && f.validation_regex) {
    try { if (!new RegExp(f.validation_regex).test(v)) return `${f.name}: invalid format`; } catch {}
  }
  // A pasted or typo'd value that is not in the list still built a taxonomy string — silently,
  // because buildTaxString falls back to the raw text when no dictionary entry matches. That is
  // exactly the mis-tagging this tool exists to prevent, so it is an error, not a shrug.
  if (v && options && !options.includes(v)) return `${f.name}: "${v}" is not an allowed value`;
  return null;
}

/**
 * Coerce whatever a date column arrives as into the ISO form everything downstream assumes.
 *
 * `formatDate` splits on "-" and hands back the raw text when that fails, so an Excel column
 * formatted 05/01/2024 — or pasted as the serial number 45296 — went into the taxonomy string
 * verbatim, silently and without an error. Returns null when the value is not a date at all,
 * which is what makes it reportable rather than invisible.
 */
export function normaliseDate(raw) {
  // A real Date arrives when a sheet cell is read with cellDates on.
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    return `${raw.getFullYear()}-${String(raw.getMonth() + 1).padStart(2, '0')}-${String(raw.getDate()).padStart(2, '0')}`;
  }
  const s = String(raw ?? '').trim();
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  const iso = (y, m, d) => {
    // Two-digit years, as a spreadsheet's display format often gives them. Excel's own
    // pivot: 00–29 is this century, 30–99 the last.
    let yr = Number(y);
    if (String(y).length <= 2) yr = yr <= 29 ? 2000 + yr : 1900 + yr;
    const mo = Number(m), dy = Number(d);
    if (!(yr >= 1900 && yr <= 2200) || !(mo >= 1 && mo <= 12) || !(dy >= 1 && dy <= 31)) return null;
    return `${String(yr).padStart(4, '0')}-${String(mo).padStart(2, '0')}-${String(dy).padStart(2, '0')}`;
  };

  // Excel's serial day count, as pasted from an unformatted cell. Day 60 is Excel's
  // non-existent 29 Feb 1900, which is why the epoch below is 30 Dec 1899.
  if (/^\d{5}$/.test(s)) {
    const d = new Date(Date.UTC(1899, 11, 30) + Number(s) * 86400000);
    return iso(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
  }

  let m;
  if ((m = s.match(/^(\d{4})[/.](\d{1,2})[/.](\d{1,2})$/)))   return iso(m[1], m[2], m[3]);
  if ((m = s.match(/^(\d{4})(\d{2})(\d{2})$/)))               return iso(m[1], m[2], m[3]);
  if ((m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/))) {
    // Read day-first, matching the locale this tool is used in. A value that can only be
    // month-first (13/01/2024 the other way round is not a month) falls back to that reading
    // rather than being rejected.
    const [, a, b, y] = m;
    return iso(y, b, a) || iso(y, a, b);
  }
  return null;
}

function formatDate(raw, fmt) {
  if (!raw) return '';
  const [yr, mo, dy] = raw.split('-');
  if (!yr || !mo || !dy) return raw;
  switch (fmt) {
    case 'YYYYMMDD':   return `${yr}${mo}${dy}`;
    case 'DDMMYYYY':   return `${dy}${mo}${yr}`;
    case 'MM-DD-YYYY': return `${mo}-${dy}-${yr}`;
    case 'DD-MM-YYYY': return `${dy}-${mo}-${yr}`;
    case 'YYYY-MM-DD': return `${yr}-${mo}-${dy}`;
    default:           return raw;
  }
}

/**
 * How a date reads in the grid: day first, as it is written here and as the cell editor
 * displays it.
 *
 * Dates are stored ISO because that is the only form the date input and the sorting understand,
 * but showing the stored form meant a cell said 2025-11-12 while the editor above it said
 * 12/11/2025 — the same date, apparently changing shape on being saved. The taxonomy string is
 * a separate question and stays on the field's configured format (see `formatDate`).
 */
export function displayDate(raw) {
  if (!raw) return '';
  const m = String(raw).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(raw);
}

export function buildTaxString(taxonomy, values, dictValues = {}) {
  if (!taxonomy?.fields?.length) return '';
  const fdel  = taxonomy.field_delimiter || '_';
  const kvdel = taxonomy.kv_delimiter    || '=';
  return taxonomy.fields.map(f => {
    const raw = values[f.id] || '';
    let val;
    if (f.field_type === 'date') {
      val = formatDate(raw, f.date_format || 'YYYYMMDD');
    } else if (f.field_type === 'dropdown' && f.dict_list_id && raw) {
      const entry = (dictValues[f.dict_list_id] || []).find(e => e.value === raw);
      val = (entry?.code) || raw;
    } else {
      val = raw;
    }
    if (!val) return null;
    return taxonomy.taxonomy_type === 'keyvalue' && f.field_key
      ? `${f.field_key}${kvdel}${val}`
      : val;
  }).filter(Boolean).join(fdel);
}

const lbl = (x = {}) => ({ fontSize: 10, fontWeight: 600, letterSpacing: '0.09em', color: C.muted, textTransform: 'uppercase', ...x });

/**
 * Parse clipboard TSV the way Excel writes it.
 *
 * Splitting on \t and \n is wrong the moment a cell contains either: Excel quotes such a cell
 * and doubles any quote inside it, so a naive split scatters one cell across several columns.
 * Campaign names with line breaks hit this regularly.
 */
export function parseTSV(text) {
  const s = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const out = [];
  let row = [], cell = '', quoted = false, cellStarted = false;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (quoted) {
      if (ch === '"') {
        if (s[i + 1] === '"') { cell += '"'; i++; }   // "" is a literal quote
        else quoted = false;
      } else cell += ch;
      continue;
    }
    if (ch === '"' && !cellStarted) { quoted = true; cellStarted = true; continue; }
    if (ch === '\t')      { row.push(cell); cell = ''; cellStarted = false; continue; }
    if (ch === '\n')      { row.push(cell); out.push(row); row = []; cell = ''; cellStarted = false; continue; }
    cell += ch; cellStarted = true;
  }
  if (cell !== '' || row.length) { row.push(cell); out.push(row); }
  return out;
}

/** Clipboard write with the execCommand fallback for browsers that refuse the async API. */
export async function writeClipboard(text) {
  try { await navigator.clipboard.writeText(text); return; } catch {}
  const ta = document.createElement('textarea');
  ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
  document.body.appendChild(ta); ta.select();
  document.execCommand('copy');
  document.body.removeChild(ta);
}

/**
 * A column filter is either a checklist of values or a text rule, mirroring Excel's two
 * halves of the same dropdown.
 *
 *   { kind: 'values', set: Set<string> }
 *   { kind: 'text', op: 'contains'|'notContains'|'begins'|'ends'|'equals', q: string }
 */
export const TEXT_OPS = [
  { op: 'contains',    label: 'Contains' },
  { op: 'notContains', label: 'Does not contain' },
  { op: 'begins',      label: 'Begins with' },
  { op: 'ends',        label: 'Ends with' },
  { op: 'equals',      label: 'Equals' },
];

export function matchesFilter(val, spec) {
  if (!spec) return true;
  if (spec.kind === 'values') return spec.set.has(val);
  const v = val.toLowerCase(), q = (spec.q || '').toLowerCase();
  if (!q) return true;
  switch (spec.op) {
    case 'notContains': return !v.includes(q);
    case 'begins':      return v.startsWith(q);
    case 'ends':        return v.endsWith(q);
    case 'equals':      return v === q;
    default:            return v.includes(q);
  }
}

/**
 * Continue a series the way Excel's fill handle does.
 *
 * Given the source values in order, returns a function (n) → the nth value after them. Two
 * plain numbers, or a shared prefix ending in digits ("AD_01", "AD_02"), extend by their
 * common step; anything else just repeats, cycling if the source was several cells long.
 * `back` continues in the other direction, for a fill dragged upwards or leftwards.
 */
export function seriesFor(src, back = false) {
  const dir = back ? -1 : 1;
  const cycle = (n) => {
    // Walking backwards reads the source from its far end, so the block abutting the source
    // continues it rather than restarting it.
    const i = back ? (src.length - 1 - (n % src.length)) : (n % src.length);
    return src[((i % src.length) + src.length) % src.length];
  };
  if (src.length < 2 || src.some(v => v == null || String(v).trim() === '')) return cycle;

  const nums = src.map(v => Number(String(v).trim()));
  if (nums.every(n => Number.isFinite(n))) {
    const step = nums[1] - nums[0];
    if (step !== 0 && nums.every((n, i) => i === 0 || n - nums[i - 1] === step)) {
      const anchor = back ? nums[0] : nums[nums.length - 1];
      return (n) => String(anchor + step * dir * (n + 1));
    }
  }

  // Shared text prefix with a trailing number: "Q1_AD_07" → "Q1_AD_08". Zero padding is kept
  // at the width it arrived in, because these are identifiers and 08 is not 8.
  const parts = src.map(v => String(v).match(/^(.*?)(\d+)$/));
  if (parts.every(Boolean) && parts.every(p => p[1] === parts[0][1])) {
    const seq = parts.map(p => Number(p[2]));
    const step = seq[1] - seq[0];
    if (step !== 0 && seq.every((n, i) => i === 0 || n - seq[i - 1] === step)) {
      const width = parts[0][2].length;
      const anchor = back ? seq[0] : seq[seq.length - 1];
      return (n) => {
        const next = anchor + step * dir * (n + 1);
        return next < 0 ? cycle(n) : parts[0][1] + String(next).padStart(width, '0');
      };
    }
  }
  return cycle;
}

/** Excel's own comparison: blanks always sink, numbers compare numerically, text naturally. */
function compareValues(a, b, mult) {
  if (a === b) return 0;
  if (a === '') return 1;
  if (b === '') return -1;
  const na = Number(a), nb = Number(b);
  if (!Number.isNaN(na) && !Number.isNaN(nb) && a.trim() !== '' && b.trim() !== '') return (na - nb) * mult;
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }) * mult;
}

// Cell chrome. Module scope so the memoized row below can use it without taking a new function
// identity on every render of the grid.
const TAX_COL = -1;
const TDcell = (hl, fl, x = {}) => ({
  padding: 0, verticalAlign: 'middle',
  borderBottom: `1px solid ${C.borderSoft}`, borderRight: `1px solid ${C.borderSoft}`,
  background: fl ? `${C.success}22` : hl ? C.brassWash : 'transparent',
  transition: 'background .08s', position: 'relative', ...x,
});
const stickyNum = (bg) => ({ position: 'sticky', left: 0,  zIndex: 1, background: bg });
const stickyTax = (bg) => ({ position: 'sticky', left: 36, zIndex: 1, background: bg, borderRight: `1px solid ${C.border}` });

const THcell = (x = {}) => ({
  padding: '6px 8px', fontWeight: 600, fontSize: 10, letterSpacing: '0.07em',
  color: C.muted, textTransform: 'uppercase', textAlign: 'left',
  borderBottom: `1px solid ${C.border}`, borderRight: `1px solid ${C.borderSoft}`,
  background: C.s2, userSelect: 'none', whiteSpace: 'nowrap',
  position: 'sticky', top: 0, zIndex: 2, ...x,
});
// The row-number and taxonomy columns stay put while the field columns scroll sideways — a wide
// taxonomy is unreadable if the string it builds scrolls off with it.
const STICKY_NUM_HEAD = { position: 'sticky', left: 0,  top: 0, zIndex: 4 };
const STICKY_TAX_HEAD = { position: 'sticky', left: 36, top: 0, zIndex: 4, borderRight: `1px solid ${C.border}` };

function SortBtn({ colKey, sortSpec, ctxRef }) {
  const rank = sortSpec.findIndex(s => s.colKey === colKey);
  const active = rank >= 0;
  const dir = active ? sortSpec[rank].dir : null;
  return (
    <span
      onClick={e => { e.stopPropagation(); ctxRef.current.toggleSort(colKey, e.shiftKey); }}
      title={!active ? 'Sort ascending · Shift-click to add to the current sort'
        : dir === 'asc' ? 'Sort descending' : 'Clear this sort'}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 1,
        minWidth: 14, height: 14, padding: active && sortSpec.length > 1 ? '0 2px' : 0,
        cursor: 'pointer', borderRadius: 6, fontSize: 9,
        color: active ? C.brass : C.muted, fontWeight: active ? 700 : 400,
        background: active ? C.brassWash : 'transparent',
        border: active ? `1px solid ${C.brass}55` : '1px solid transparent',
      }}
    >
      {active ? (dir === 'asc' ? '▲' : '▼') : '↕'}
      {/* The key's place in a multi-column sort — meaningless, and noise, when there is one. */}
      {active && sortSpec.length > 1 && (
        <span style={{ fontFamily: C.mono, fontSize: 8 }}>{rank + 1}</span>
      )}
    </span>
  );
}

function FilterBtn({ colKey, activeFilters, filtersOn, ctxRef }) {
  const isActive = activeFilters[colKey] != null;
  // Ctrl+Shift+L hides the controls, as in Excel. An active filter keeps its control on screen
  // regardless — hiding the only way to clear it would strand the sheet.
  if (!filtersOn && !isActive) return null;
  return (
    <span
      onClick={e => ctxRef.current.openFilterFor(colKey, e)}
      title={isActive ? 'Filter active — click to change' : 'Filter column'}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 16, height: 14, cursor: 'pointer', flexShrink: 0, borderRadius: 6,
        color: isActive ? C.brass : C.muted,
        fontSize: 9, fontWeight: isActive ? 700 : 400,
        background: isActive ? C.brassWash : 'transparent',
        border: isActive ? `1px solid ${C.brass}55` : '1px solid transparent',
      }}
    >
      {isActive ? '▼' : '▽'}
    </span>
  );
}

function ResizeHandle({ colKey, defaultWidth, colWidths, ctxRef }) {
  return (
    <span
      onClick={e => e.stopPropagation()}
      onMouseDown={e => {
        e.preventDefault(); e.stopPropagation();
        const startX = e.clientX;
        const startW = colWidths[colKey] || defaultWidth;
        const move = ev => ctxRef.current.setColWidths(w => ({ ...w, [colKey]: Math.max(60, startW + ev.clientX - startX) }));
        const up = () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
        window.addEventListener('mousemove', move);
        window.addEventListener('mouseup', up);
      }}
      onDoubleClick={e => { e.stopPropagation(); ctxRef.current.setColWidths(w => { const n = { ...w }; delete n[colKey]; return n; }); }}
      title="Drag to resize · double-click to reset"
      style={{ position: 'absolute', top: 0, right: -3, width: 7, height: '100%', cursor: 'col-resize', zIndex: 5 }}
    />
  );
}

/**
 * The column header, memoized and held apart from the body.
 *
 * These pieces used to be declared inside the grid's render, so every keystroke created new
 * component types and React remounted all ~20 columns of them — a fixed ~30ms per edit that had
 * nothing to do with how many rows the sheet held. Memoized on what it displays, the header now
 * re-renders when a sort, filter or column width changes, and not when a cell does.
 */
const GridHead = memo(function GridHead({
  fields, sortSpec, activeFilters, filtersOn, colWidths, hasActiveFilters, ctxRef, outputLabel = 'Taxonomy string',
}) {
  return (
    <thead>
      <tr>
        <th style={THcell({ width: 36, textAlign: 'center', ...STICKY_NUM_HEAD })}>
          {hasActiveFilters ? (
            <span
              onClick={() => ctxRef.current.clearFilters()}
              title="Clear all filters"
              style={{ cursor: 'pointer', color: C.brass, fontSize: 9, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}
            >✕</span>
          ) : '#'}
        </th>
        <th
          style={THcell({ color: activeFilters['__tax__'] ? C.brass : C.muted, cursor: 'pointer', ...STICKY_TAX_HEAD })}
          onClick={() => ctxRef.current.selectColumn(TAX_COL)}
          title="Click to select column"
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4 }}>
            <span style={{ color: activeFilters['__tax__'] ? C.brass : undefined }}>{outputLabel}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
              <SortBtn colKey="__tax__" sortSpec={sortSpec} ctxRef={ctxRef} />
              <FilterBtn colKey="__tax__" activeFilters={activeFilters} filtersOn={filtersOn} ctxRef={ctxRef} />
            </span>
          </div>
          <ResizeHandle colKey="__tax__" defaultWidth={520} colWidths={colWidths} ctxRef={ctxRef} />
        </th>
        {fields.map((f, fi) => (
          <th key={f.id}
            style={THcell({ cursor: 'pointer' })}
            onClick={() => ctxRef.current.selectColumn(fi)}
            title="Click to select column"
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 4, overflow: 'hidden', minWidth: 0 }}>
                <span style={{ fontSize: 9, color: C.brass, flexShrink: 0 }}>
                  {f.field_type === 'dropdown' ? 'DD' : f.field_type === 'date' ? 'DT' : 'TX'}
                </span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', color: activeFilters[String(f.id)] ? C.brass : undefined }}>
                  {f.name}
                </span>
                {f.is_required && <span style={{ color: C.danger, flexShrink: 0 }}>•</span>}
              </div>
              <span style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                <SortBtn colKey={String(f.id)} sortSpec={sortSpec} ctxRef={ctxRef} />
                <FilterBtn colKey={String(f.id)} activeFilters={activeFilters} filtersOn={filtersOn} ctxRef={ctxRef} />
              </span>
            </div>
            <ResizeHandle colKey={String(f.id)} defaultWidth={Math.max(120, f.name.length * 9 + 40)} colWidths={colWidths} ctxRef={ctxRef} />
          </th>
        ))}
      </tr>
    </thead>
  );
});

/**
 * One row of the sheet, memoized.
 *
 * Typing used to re-render every visible row — ~50 rows × ~20 cells for one keystroke —
 * because the whole body was rebuilt inline. `setCell` replaces only the row it writes to, so
 * with the row extracted and memoized a keystroke re-renders exactly one row.
 *
 * That only holds if the props stay comparable, which shapes the interface:
 *
 *  - Handlers arrive through `ctxRef`, a ref whose contents are rebuilt every render. Its
 *    identity never changes, so it never defeats the memo, and reading `ctxRef.current` at
 *    event time means a row that has not re-rendered still calls today's closures rather than
 *    a stale one. Passing the callbacks directly would break both properties.
 *  - Selection, fill preview and the edit cursor arrive as plain numbers describing the
 *    rectangle, not as objects. A row works out its own highlighting from them. Selection
 *    changes do re-render every visible row, which is correct — the highlight moved — and
 *    cheap now that a row is text rather than twenty controls.
 */
const GridRow = memo(function GridRow({
  row, ri, pos, taxStr, fields, dictValues, readOnly, ctxRef,
  p1, p2, f1, f2, anchorRi, anchorFi, fp1, fp2, ff1, ff2,
  editFi, rowSelected, rowHovered, rowErrs, flashSet, cornerFi, taxWidth, reviewFields, outputHint,
}) {
  const rowBg = rowSelected ? C.brassWash : pos % 2 === 0 ? C.surface : C.s2;
  const inRect = (fi) => pos >= p1 && pos <= p2 && fi >= f1 && fi <= f2;
  const inFill = (fi) => pos >= fp1 && pos <= fp2 && fi >= ff1 && fi <= ff2;

  const txHl   = inRect(TAX_COL);
  const txAnch = anchorRi === ri && anchorFi === TAX_COL;

  return (
    <tr data-row-id={row.id} style={{ background: rowBg, outline: rowSelected ? `1px solid ${C.brass}44` : 'none' }}>
      <td style={{
          ...TDcell(false, false, { textAlign: 'center', fontSize: 11, padding: '4px 0', borderRight: `1px solid ${C.border}`, userSelect: 'none' }),
          cursor: readOnly ? 'default' : 'pointer',
          fontFamily: C.mono,
          ...stickyNum(rowBg),
          color: rowSelected ? C.brass : rowHovered ? C.text : C.muted,
          background: rowSelected ? C.brassWash : rowHovered ? C.borderSoft : rowBg,
          fontWeight: rowSelected || rowHovered ? 600 : 400,
          borderLeft: rowSelected ? `2px solid ${C.brass}` : '2px solid transparent',
        }}
        onMouseEnter={() => ctxRef.current.setHoverRow(ri)}
        onMouseLeave={() => ctxRef.current.setHoverRow(null)}
        onClick={e => ctxRef.current.onRowNumberClick(e, ri)}
        onContextMenu={e => {
          if (readOnly) return;
          e.preventDefault();
          ctxRef.current.setCtxMenu({ ri, x: e.clientX, y: e.clientY });
        }}
      >
        {ri + 1}
      </td>

      <td style={{
          ...TDcell(txHl, false, { maxWidth: taxWidth }),
          ...stickyTax(txHl ? C.brassWash : rowBg),
          outline: txAnch ? `2px solid ${C.brass}` : txHl ? `1px solid ${C.brass}55` : 'none',
          outlineOffset: -1,
          cursor: 'cell',
        }}
        onMouseDown={e => ctxRef.current.onCellMouseDown(e, ri, TAX_COL)}
        onMouseEnter={() => ctxRef.current.onCellMouseEnter(ri, TAX_COL, pos)}
        onDoubleClick={() => ctxRef.current.onTaxDoubleClick(taxStr)}
        data-output-cell="true"
        title={[outputHint, taxStr ? 'Double-click to copy · Ctrl+C to copy the selection' : ''].filter(Boolean).join(' · ')}
      >
        <span style={{ ...mono, fontSize: 10, color: taxStr ? C.code : C.muted, display: 'block', padding: '4px 8px', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
          {outputHint && taxStr && <span aria-label={outputHint}>⚠ </span>}{taxStr || outputHint || ''}
        </span>
      </td>

      {fields.map((f, fi) => {
        const key     = `${ri},${fi}`;
        const hl      = inRect(fi);
        const anch    = anchorRi === ri && anchorFi === fi;
        const fl      = flashSet.has(key);
        const isEdit  = editFi === fi;
        const val     = row.values[f.id] || '';
        const cellErr = !readOnly || reviewFields ? (rowErrs?.[fi] ?? null) : null;
        const cellReview = reviewFields?.[f.id]?.reason;
        const isDrop  = f.field_type === 'dropdown';
        const isDate  = f.field_type === 'date';
        // Dates are stored ISO and shown day-first; everything else shows what is stored.
        const shown   = isDate ? displayDate(val) : val;
        const dropVals = isDrop
          ? (f.dict_list_id ? (dictValues[f.dict_list_id] || []) : (f.dictionary || []).map(d => ({ id: d.id, value: d.value })))
          : [];

        return (
          <td key={f.id}
            data-field-id={f.id}
            aria-label={`${f.name}, row ${ri + 1}`}
            aria-invalid={!!cellErr}
            className={cellReview ? 'sg-review' : undefined}
            title={cellErr || cellReview || undefined}
            style={{
              ...TDcell(hl, fl),
              outline: anch ? `2px solid ${C.brass}` : hl ? `1px solid ${C.brass}55` : 'none',
              outlineOffset: -1,
              cursor: 'cell',
              background: cellErr ? `${C.danger}22` : cellReview ? T.warnWash
                : inFill(fi) ? `${C.brass}1A`
                : anch ? C.brassWash : TDcell(hl, fl).background,
            }}
            onMouseDown={e => ctxRef.current.onCellMouseDown(e, ri, fi)}
            // A dictionary or date cell opens its picker on a click, the way the old <select>
            // did. On click rather than mousedown, so dragging a selection across these columns
            // still selects instead of opening an editor under the cursor.
            onClick={() => {
              if (isDrop) ctxRef.current.openEditor(ri, fi, { ifNotDragged: true });
              else if (isDate) ctxRef.current.openEditor(ri, fi, { ifNotDragged: true, focus: true, picker: true });
            }}
            onMouseEnter={() => ctxRef.current.onCellMouseEnter(ri, fi, pos)}
            onDoubleClick={() => { if (!isDrop && !isDate) ctxRef.current.openEditor(ri, fi, { focus: true }); }}
          >
            {readOnly ? (
              <span style={{ display: 'block', padding: '4px 8px', fontSize: 12, color: val ? C.text : C.muted }}>{shown || '—'}</span>
            ) : isDrop && isEdit ? (
              <ComboEditor
                value={val}
                options={dropVals}
                initialQuery={ctxRef.current.pendingQuery()}
                inputRef={el => { ctxRef.current.inputRefs.current[key] = el; }}
                onCommit={v => ctxRef.current.commitCell(ri, fi, v)}
                onCancel={() => ctxRef.current.closeEditor()}
                onNavigate={dir => ctxRef.current.navigateLater(ri, fi, dir)}
              />
            ) : isEdit ? (
              f.field_type === 'date' ? (
                <input
                  ref={el => { ctxRef.current.inputRefs.current[key] = el; }}
                  type="date"
                  value={normaliseDate(val) || ''}
                  autoFocus
                  onChange={e => ctxRef.current.setCell(ri, fi, e.target.value)}
                  onBlur={() => ctxRef.current.blurEditor()}
                  onKeyDown={e => ctxRef.current.onEditorKeyDown(e, ri, fi)}
                  style={{ width: '100%', background: C.surface, border: 'none', color: C.text, fontSize: 12, fontFamily: 'inherit', padding: '4px 8px', outline: 'none', display: 'block' }}
                />
              ) : (
                // A textarea rather than an input purely so Alt+Enter can put a real line break
                // in a cell, as in Excel. It is held at one row high, so it reads as a cell
                // editor and the grid's row maths still hold.
                <textarea
                  ref={el => { ctxRef.current.inputRefs.current[key] = el; }}
                  rows={1}
                  value={val}
                  autoFocus
                  maxLength={f.max_length || undefined}
                  onChange={e => ctxRef.current.setCell(ri, fi, e.target.value)}
                  onBlur={() => ctxRef.current.blurEditor()}
                  onKeyDown={e => ctxRef.current.onEditorKeyDown(e, ri, fi, { allowAltEnter: true })}
                  style={{ width: '100%', background: C.surface, border: 'none', color: C.text, fontSize: 12, fontFamily: 'inherit', padding: '4px 8px', outline: 'none', display: 'block', resize: 'none', lineHeight: '20px', height: 28, overflow: 'hidden', boxSizing: 'border-box' }}
                />
              )
            ) : (
              // Idle cells are text, dictionary columns included: a control per cell is what made
              // a large sheet slow to open and slow to type in. The caret is the affordance that
              // says this one opens a list.
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px', fontSize: 12, color: val ? C.text : C.muted, whiteSpace: 'nowrap', overflow: 'hidden' }}>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{shown || ''}</span>
                {isDrop && <span aria-hidden="true" style={{ fontSize: 8, color: C.muted, flexShrink: 0 }}>▼</span>}
                {isDate && <span aria-hidden="true" style={{ fontSize: 10, color: C.muted, flexShrink: 0 }}>▤</span>}
              </span>
            )}
            {!readOnly && cornerFi === fi && (
              <span
                title="Drag to fill · double-click to fill to the bottom of the data"
                onMouseDown={e => { e.preventDefault(); e.stopPropagation(); ctxRef.current.startFill(); }}
                onDoubleClick={e => { e.preventDefault(); e.stopPropagation(); ctxRef.current.fillToBottom(); }}
                style={{
                  position: 'absolute', right: -3, bottom: -3, width: 7, height: 7,
                  background: C.brass, border: `1px solid ${C.surface}`,
                  cursor: 'crosshair', zIndex: 3,
                }}
              />
            )}
          </td>
        );
      })}
    </tr>
  );
});

// ── Dictionary cell editor ────────────────────────────────────────────────────
//
// A dictionary column is a native <select> no longer. Two reasons, in order of how much they
// hurt:
//
//   1. A <select> carries its whole option list in the DOM. Every dropdown cell mounting one
//      meant ~50 visible rows × ~10 dictionary columns × the dictionary — measured at 200,250
//      <option> nodes and a 6.8s open on a 500-row sheet. Only the cell being edited mounts an
//      editor now, so the sheet holds one list at most.
//   2. A native list of 800 values cannot be searched. Excel gives data validation type-ahead;
//      this does the same — the input filters as you type, and what you type is what commits
//      if it matches nothing, so a pasted or typo'd value still lands in the cell and is
//      flagged by validation rather than silently refused.
//
// The list is fixed-positioned and measured, like the context menu: it hangs outside the
// grid's scroll container, which would otherwise clip it.
function ComboEditor({ value, options, initialQuery, inputRef, onCommit, onCancel, onNavigate }) {
  const [query, setQuery] = useState(initialQuery ?? '');
  const [hi, setHi] = useState(0);
  const [pos, setPos] = useState(null);
  const wrapRef = useRef(null);
  const listRef = useRef(null);
  const positioned = pos !== null;
  const MAX_SHOWN = 200;

  // Typed text filters; an untouched editor lists everything, so opening a cell still shows the
  // whole column vocabulary the way the old dropdown did.
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => o.value.toLowerCase().includes(q));
  }, [options, query]);
  const shown = matches.length > MAX_SHOWN ? matches.slice(0, MAX_SHOWN) : matches;

  useEffect(() => { setHi(0); }, [query]);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // Include Clear value and the empty-state text when placing the menu.
    // After it mounts, measure its content instead of estimating row heights.
    const contentHeight = listRef.current ? listRef.current.scrollHeight + 2 : 34 * (Math.max(1, shown.length) + 1) + 8;
    const H = Math.min(260, contentHeight);
    const below = window.innerHeight - r.bottom;
    setPos({
      left: Math.max(8, Math.min(r.left, window.innerWidth - 260 - 8)),
      top:  below < H + 8 ? Math.max(8, r.top - H - 2) : r.bottom + 2,
      width: Math.max(180, r.width),
      maxHeight: 260,
    });
  }, [shown.length, positioned]);

  // Keep the highlighted row in view when arrowing through a long list.
  useEffect(() => {
    listRef.current?.querySelector('[data-hi="1"]')?.scrollIntoView({ block: 'nearest' });
  }, [hi]);

  const commit = (v) => onCommit(v);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown')      { e.preventDefault(); e.stopPropagation(); setHi(h => Math.min(shown.length - 1, h + 1)); }
    else if (e.key === 'ArrowUp')   { e.preventDefault(); e.stopPropagation(); setHi(h => Math.max(0, h - 1)); }
    else if (e.key === 'Enter')     { e.preventDefault(); e.stopPropagation(); commit(shown[hi]?.value ?? query.trim()); onNavigate('down'); }
    else if (e.key === 'Tab')       { e.preventDefault(); e.stopPropagation(); commit(shown[hi]?.value ?? query.trim()); onNavigate(e.shiftKey ? 'left' : 'right'); }
    else if (e.key === 'Escape')    { e.stopPropagation(); onCancel(); }
    else if (e.key === 'Delete' && !query) { e.preventDefault(); e.stopPropagation(); commit(''); }
    else e.stopPropagation();   // ordinary typing belongs to the input, not the grid
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        value={query}
        autoFocus
        placeholder={value || '—'}
        onChange={e => setQuery(e.target.value)}
        onKeyDown={onKeyDown}
        // Clicking a value must not read as clicking away, so the list suppresses the blur.
        onBlur={onCancel}
        style={{ width: '100%', background: C.surface, border: 'none', color: C.text, fontSize: 12,
                 fontFamily: 'inherit', padding: '4px 8px', outline: 'none', display: 'block' }}
      />
      {pos && (
        <div ref={listRef} style={{
          position: 'fixed', top: pos.top, left: pos.left, width: pos.width, maxHeight: pos.maxHeight,
          overflowY: 'auto', zIndex: 9998, background: C.surface,
          border: `1px solid ${C.border}`, borderRadius: T.rMd, boxShadow: T.shadow,
        }}>
          <div
            onMouseDown={e => { e.preventDefault(); commit(''); }}
            style={{ padding: '6px 10px', fontSize: 12, color: C.muted, cursor: 'pointer',
                     borderBottom: `1px solid ${C.borderSoft}` }}
          >
            Clear value
          </div>
          {shown.map((o, i) => (
            <div key={o.id ?? o.value}
              data-hi={i === hi ? '1' : undefined}
              onMouseDown={e => { e.preventDefault(); commit(o.value); }}
              onMouseEnter={() => setHi(i)}
              style={{
                padding: '6px 10px', fontSize: 12, cursor: 'pointer',
                background: i === hi ? C.brassWash : 'transparent',
                color: o.value === value ? C.text : C.dim,
                fontWeight: o.value === value ? 600 : 400,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}
            >
              {o.value}
            </div>
          ))}
          {!shown.length && (
            <div style={{ padding: '8px 10px', fontSize: 12, color: C.muted }}>
              No match — Enter keeps what you typed
            </div>
          )}
          {matches.length > shown.length && (
            <div style={{ padding: '6px 10px', fontSize: 11, color: C.muted, borderTop: `1px solid ${C.borderSoft}` }}>
              {matches.length - shown.length} more — keep typing to narrow
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Filter Dropdown ───────────────────────────────────────────────────────────
function FilterDropdown({ allValues, active, onApply, onClose, onSort, sortDir, anchorRect }) {
  const [search,  setSearch]  = useState('');
  const [checked, setChecked] = useState(
    () => active?.kind === 'values' ? new Set(active.set) : new Set(allValues));
  // A text rule and a checklist are alternatives, as in Excel: writing a rule is what selects
  // it, and clearing the box hands the column back to the checklist.
  const [textOp, setTextOp] = useState(active?.kind === 'text' ? active.op : 'contains');
  const [textQ,  setTextQ]  = useState(active?.kind === 'text' ? active.q  : '');
  const dropRef   = useRef();
  const searchRef = useRef();

  useEffect(() => {
    setTimeout(() => searchRef.current?.focus(), 0);
    const onDown = (e) => { if (!dropRef.current?.contains(e.target)) onClose(); };
    const onEsc  = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onEsc); };
  }, []);

  const searchLow  = search.toLowerCase();
  const filtered   = allValues.filter(v => (v || '(blank)').toLowerCase().includes(searchLow));
  const allChecked = filtered.length > 0 && filtered.every(v => checked.has(v));
  const someChecked = !allChecked && filtered.some(v => checked.has(v));

  const toggle = (v) => setChecked(prev => { const n = new Set(prev); n.has(v) ? n.delete(v) : n.add(v); return n; });
  const toggleAll = () => {
    if (allChecked) setChecked(prev => { const n = new Set(prev); filtered.forEach(v => n.delete(v)); return n; });
    else            setChecked(prev => { const n = new Set(prev); filtered.forEach(v => n.add(v)); return n; });
  };

  const handleOK = () => {
    if (textQ.trim()) { onApply({ kind: 'text', op: textOp, q: textQ.trim() }); return; }
    const isAll = allValues.every(v => checked.has(v));
    onApply(isAll ? null : { kind: 'values', set: new Set(checked) });
  };

  const top  = Math.min(anchorRect.bottom + 4, Math.max(8, window.innerHeight - 430));
  const left = Math.min(anchorRect.left, window.innerWidth - 260);

  const menuRow = {
    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
    padding: '6px 12px', background: 'transparent', border: 'none',
    color: C.text, fontSize: 12, textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
  };

  return (
    <div ref={dropRef} style={{
      position: 'fixed', top, left, zIndex: 10000, width: 252,
      background: C.surface, border: `1px solid ${C.border}`,
      borderRadius: T.rMd, boxShadow: T.shadow,
      fontFamily: 'inherit', overflow: 'hidden',
    }}>
      {/* Sorting lives in this menu too — it is where an Excel user looks for it, and the
          header's ↕ control is easy to miss on a narrow column. */}
      <div style={{ padding: '4px 0', borderBottom: `1px solid ${C.border}` }}>
        <button style={{ ...menuRow, fontWeight: sortDir === 'asc' ? 600 : 400 }}
          onMouseEnter={e => e.currentTarget.style.background = C.s2}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          onClick={() => onSort('asc')}>
          <span style={{ color: C.muted, width: 12 }}>▲</span> Sort A → Z
        </button>
        <button style={{ ...menuRow, fontWeight: sortDir === 'desc' ? 600 : 400 }}
          onMouseEnter={e => e.currentTarget.style.background = C.s2}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          onClick={() => onSort('desc')}>
          <span style={{ color: C.muted, width: 12 }}>▼</span> Sort Z → A
        </button>
      </div>

      <div style={{ padding: '8px 10px', borderBottom: `1px solid ${C.border}`, display: 'flex', gap: 5 }}>
        <select
          value={textOp}
          onChange={e => setTextOp(e.target.value)}
          style={{ background: C.s2, border: `1px solid ${C.border}`, borderRadius: T.rSm, padding: '5px 4px', color: C.text, fontSize: 11, fontFamily: 'inherit', outline: 'none', flexShrink: 0, maxWidth: 104 }}
        >
          {TEXT_OPS.map(o => <option key={o.op} value={o.op}>{o.label}</option>)}
        </select>
        <input
          value={textQ}
          onChange={e => setTextQ(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleOK(); }}
          placeholder="text…"
          style={{ flex: 1, minWidth: 0, background: C.s2, border: `1px solid ${C.border}`, borderRadius: T.rSm, padding: '5px 8px', color: C.text, fontSize: 12, fontFamily: 'inherit', outline: 'none' }}
        />
      </div>

      <div style={{ padding: '8px 10px', borderBottom: `1px solid ${C.border}`, opacity: textQ.trim() ? 0.45 : 1 }}>
        <input
          ref={searchRef}
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search values…"
          disabled={!!textQ.trim()}
          style={{ width: '100%', background: C.s2, border: `1px solid ${C.border}`, borderRadius: T.rSm, padding: '5px 9px', color: C.text, fontSize: 12, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }}
        />
      </div>

      <div style={{ padding: '5px 10px', borderBottom: `1px solid ${C.border}`, opacity: textQ.trim() ? 0.45 : 1 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, color: C.dim, userSelect: 'none' }}>
          <input
            type="checkbox"
            checked={allChecked}
            disabled={!!textQ.trim()}
            ref={el => { if (el) el.indeterminate = someChecked; }}
            onChange={toggleAll}
            style={{ accentColor: C.ink, width: 13, height: 13, flexShrink: 0 }}
          />
          (Select all)
        </label>
      </div>

      <div style={{ maxHeight: 180, overflowY: 'auto', padding: '3px 0', opacity: textQ.trim() ? 0.45 : 1, pointerEvents: textQ.trim() ? 'none' : 'auto' }}>
        {filtered.length === 0 ? (
          <div style={{ padding: '10px 14px', color: C.muted, fontSize: 12 }}>No matches</div>
        ) : filtered.map(v => (
          <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 14px', cursor: 'pointer', fontSize: 12, color: checked.has(v) ? C.text : C.muted, userSelect: 'none' }}
            onMouseEnter={e => e.currentTarget.style.background = C.s2}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <input type="checkbox" checked={checked.has(v)} onChange={() => toggle(v)} style={{ accentColor: C.ink, width: 13, height: 13, flexShrink: 0 }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v || <span style={{ color: C.muted, fontStyle: 'italic' }}>(blank)</span>}</span>
          </label>
        ))}
      </div>

      <div style={{ padding: '8px 10px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        {active && (
          <button onClick={() => onApply(null)}
            style={{ marginRight: 'auto', background: 'none', border: 'none', color: C.dim, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', padding: '5px 0' }}>
            Clear filter
          </button>
        )}
        <button onClick={onClose} className="o-btn o-btn-sm">Cancel</button>
        <button onClick={handleOK} className="o-btn o-btn-sm o-btn-primary">OK</button>
      </div>
    </div>
  );
}

// ── Batch Generate ────────────────────────────────────────────────────────────
// Per-field and per-batch accents come from the shared series palette.
const BATCH_COLORS = SERIES;
const BATCH_LABEL_COLORS = SERIES.slice(0, 5);

const mkBatchSelections = (fields) => {
  const s = {};
  fields.forEach(f => { s[f.id] = f.field_type === 'dropdown' ? new Set() : ''; });
  return s;
};

const computeCombinations = (fields, selections) => {
  const active = fields.map(f => {
    let vals;
    if (f.field_type === 'dropdown') {
      vals = [...(selections[f.id] || new Set())];
    } else {
      const raw = (selections[f.id] || '').trim();
      vals = raw ? raw.split(',').map(v => v.trim()).filter(Boolean) : [];
    }
    return { f, vals };
  }).filter(({ vals }) => vals.length > 0);
  if (!active.length) return [];
  let combos = [{}];
  for (const { f, vals } of active) {
    const next = [];
    for (const combo of combos)
      for (const val of vals) next.push({ ...combo, [f.id]: val });
    combos = next;
  }
  return combos;
};

function BatchPanel({ fields, dictValues, selections, onChange }) {
  const togglePill = (fieldId, value) => {
    const s = new Set(selections[fieldId]);
    if (s.has(value)) s.delete(value); else s.add(value);
    onChange({ ...selections, [fieldId]: s });
  };
  const selectAll = (f) => {
    const vals = f.dict_list_id
      ? (dictValues[f.dict_list_id] || []).map(v => v.value)
      : (f.dictionary || []).map(d => d.value);
    onChange({ ...selections, [f.id]: new Set(vals) });
  };
  const clearField = (f) => onChange({ ...selections, [f.id]: f.field_type === 'dropdown' ? new Set() : '' });

  return (
    <div>
      {fields.map((f, fi) => {
        const fcolor = BATCH_COLORS[fi % BATCH_COLORS.length];
        const dropVals = f.field_type === 'dropdown'
          ? (f.dict_list_id ? (dictValues[f.dict_list_id] || []) : (f.dictionary || []).map(d => ({ id: d.id, value: d.value })))
          : [];
        const selSet = selections[f.id];
        return (
          <div key={f.id} style={{ marginBottom: 20, paddingBottom: 18, borderBottom: `1px solid ${C.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 9 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                {f.field_key && (
                  <span style={{ background: fcolor + '22', border: `1px solid ${fcolor}55`, color: fcolor, fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 8, letterSpacing: '0.05em' }}>
                    {f.field_key}
                  </span>
                )}
                <span style={{ fontSize: 11, fontWeight: 700, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{f.name}</span>
              </div>
              {f.field_type === 'dropdown' && (
                <div style={{ display: 'flex', gap: 12 }}>
                  <button onClick={() => selectAll(f)} style={{ background: 'none', border: 'none', color: C.dim, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', padding: 0, textDecoration: 'underline' }}>All</button>
                  <button onClick={() => clearField(f)} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>Clear</button>
                </div>
              )}
            </div>
            {f.field_type === 'dropdown' ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                {dropVals.map(dv => {
                  const on = selSet instanceof Set && selSet.has(dv.value);
                  return (
                    <button key={dv.id || dv.value} onClick={() => togglePill(f.id, dv.value)} style={{
                      padding: '4px 13px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
                      fontSize: 12, fontWeight: on ? 600 : 400,
                      background: on ? fcolor + '22' : C.s2,
                      color: on ? fcolor : C.muted,
                      border: `1px solid ${on ? fcolor + '88' : C.border}`,
                    }}>{dv.value}</button>
                  );
                })}
                {dropVals.length === 0 && <span style={{ color: C.muted, fontSize: 12 }}>No values defined.</span>}
              </div>
            ) : f.field_type === 'date' ? (
              // A date column asked for dates to be typed as text here, in whatever shape the
              // user guessed at, while the grid gives the same field a calendar. Dates are
              // picked and listed instead; several of them build combinations exactly as
              // several dictionary values do.
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, alignItems: 'center' }}>
                {String(selSet || '').split(',').map(s => s.trim()).filter(Boolean).map(d => (
                  <span key={d} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12,
                    background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8,
                    padding: '4px 8px', color: C.text,
                  }}>
                    {displayDate(d)}
                    <button
                      onClick={() => onChange({
                        ...selections,
                        [f.id]: String(selSet || '').split(',').map(s => s.trim()).filter(v => v && v !== d).join(', '),
                      })}
                      title="Remove this date"
                      style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 13, lineHeight: 1, padding: 0, fontFamily: 'inherit' }}
                    >×</button>
                  </span>
                ))}
                <input
                  type="date"
                  value=""
                  onChange={e => {
                    const picked = e.target.value;
                    if (!picked) return;
                    const cur = String(selSet || '').split(',').map(s => s.trim()).filter(Boolean);
                    if (!cur.includes(picked)) onChange({ ...selections, [f.id]: [...cur, picked].join(', ') });
                  }}
                  style={{ background: C.s2, border: `1px solid ${C.border}`, borderRadius: 8, padding: '6px 10px', color: C.text, fontSize: 12, fontFamily: 'inherit', outline: 'none' }}
                />
              </div>
            ) : (
              <input
                placeholder="Value A, Value B, Value C  (comma-separated)"
                value={selSet || ''}
                onChange={e => onChange({ ...selections, [f.id]: e.target.value })}
                style={{ width: '100%', background: C.s2, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 13px', color: C.text, fontSize: 12, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function BatchGenerateModal({ taxonomy, dictValues, onGenerate, onClose, preview }) {
  const fields = taxonomy?.fields || [];
  const [batches, setBatches] = useState([{ id: uid(), selections: mkBatchSelections(fields) }]);
  const addBatch    = () => setBatches(prev => [...prev, { id: uid(), selections: mkBatchSelections(fields) }]);
  const removeBatch = (id) => setBatches(prev => prev.filter(b => b.id !== id));
  const updateBatch = (id, selections) => setBatches(prev => prev.map(b => b.id === id ? { ...b, selections } : b));
  const batchCombos = useMemo(() => batches.map(b => ({ id: b.id, combos: computeCombinations(fields, b.selections) })), [batches, fields]);
  const totalRows = batchCombos.reduce((s, b) => s + b.combos.length, 0);

  // Validate: each batch must have all required fields covered
  const batchErrors = useMemo(() => batches.map((batch, bi) => {
    const missing = fields.filter(f => {
      if (!f.is_required) return false;
      if (f.field_type === 'dropdown') {
        return !(batch.selections[f.id] instanceof Set) || batch.selections[f.id].size === 0;
      }
      return !(batch.selections[f.id] || '').trim();
    });
    return missing.length ? { bi, names: missing.map(f => f.name) } : null;
  }).filter(Boolean), [batches, fields]);

  const handleGenerate = () => {
    const allRows = batchCombos.flatMap(({ combos }) => combos.map(values => ({ id: uid(), values })));
    if (!allRows.length) return;
    onGenerate(allRows); onClose();
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, boxShadow: '0 8px 40px rgba(23,26,31,.18)', width: '100%', maxWidth: '100%', height: '96vh', display: 'flex', flexDirection: 'column', fontFamily: 'inherit' }}>
        <div style={{ padding: '14px 22px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <span style={{ fontSize: 18, color: C.warn }}>⚡</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: C.text }}>Batch Generate</div>
            <div style={{ fontSize: 11, color: C.muted }}>Create multiple batches — each with its own field selections; all combinations are appended as rows</div>
          </div>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 8, color: C.muted, cursor: 'pointer', fontSize: 12, padding: '5px 14px', fontFamily: 'inherit' }}>× Close</button>
        </div>
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px 22px 20px' }}>
            {fields.length === 0 && <div style={{ color: C.muted, fontSize: 13 }}>No fields defined for this level.</div>}
            {batches.map((batch, bi) => {
              const bcolor = BATCH_LABEL_COLORS[bi % BATCH_LABEL_COLORS.length];
              const count  = batchCombos.find(b => b.id === batch.id)?.combos.length ?? 0;
              return (
                <div key={batch.id} style={{ marginBottom: 20, border: `1px solid ${bcolor}44`, borderRadius: 12, overflow: 'hidden' }}>
                  <div style={{ background: bcolor + '18', borderBottom: `1px solid ${bcolor}33`, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: bcolor, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Batch {bi + 1}</span>
                    {count > 0 && <span style={{ fontFamily: C.mono, fontSize: 11, color: C.dim, background: bcolor + '26', borderRadius: 6, padding: '1px 8px' }}>{count} row{count !== 1 ? 's' : ''}</span>}
                    <div style={{ flex: 1 }} />
                    {batches.length > 1 && <button onClick={() => removeBatch(batch.id)} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 0, fontFamily: 'inherit' }}>×</button>}
                  </div>
                  <div style={{ padding: '16px 16px 4px' }}>
                    <BatchPanel fields={fields} dictValues={dictValues} selections={batch.selections} onChange={sel => updateBatch(batch.id, sel)} />
                  </div>
                </div>
              );
            })}
            <button onClick={addBatch} style={{ width: '100%', padding: '11px 0', background: 'none', border: `1px dashed ${C.border}`, borderRadius: 12, color: C.muted, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = C.ruleHover; e.currentTarget.style.color = C.text; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.muted; }}
            >+ Add another batch</button>
          </div>
          <div style={{ width: '50%', flexShrink: 0, display: 'flex', flexDirection: 'column', padding: '16px 18px 0', borderLeft: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: C.muted, textTransform: 'uppercase', marginBottom: 12 }}>Combination Preview</div>
            {totalRows === 0 ? (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: C.muted, gap: 14 }}>
                <span style={{ fontSize: 28, opacity: 0.4 }}>⊞</span>
                <span style={{ fontSize: 12, textAlign: 'center', lineHeight: 1.6 }}>Select values in a batch to preview combinations.</span>
              </div>
            ) : (
              <div style={{ flex: 1, overflowY: 'auto', marginBottom: 12 }}>
                {batchCombos.map(({ id, combos }, bi) => {
                  if (!combos.length) return null;
                  const bcolor = BATCH_LABEL_COLORS[bi % BATCH_LABEL_COLORS.length];
                  return (
                    <div key={id} style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: bcolor, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                        Batch {bi + 1} <span style={{ fontWeight: 400, color: C.muted }}>· {combos.length} row{combos.length !== 1 ? 's' : ''}</span>
                      </div>
                      {combos.slice(0, 100).map((combo, i) => {
                        const taxStr = preview ? preview(combo) : buildTaxString(taxonomy, combo, dictValues);
                        return (
                          <div key={i} style={{ ...mono, fontSize: 10, color: taxStr ? C.code : C.muted, padding: '4px 0', borderBottom: `1px solid ${C.border}`, wordBreak: 'break-all', lineHeight: 1.5 }}>
                            {taxStr || '—'}
                          </div>
                        );
                      })}
                      {combos.length > 100 && <div style={{ color: C.muted, fontSize: 11, padding: '6px 0' }}>… and {combos.length - 100} more</div>}
                    </div>
                  );
                })}
              </div>
            )}
            <div style={{ paddingBottom: 18, flexShrink: 0 }}>
              {totalRows > 0 && <div style={{ fontSize: 11, color: C.muted, textAlign: 'center', marginBottom: 8 }}><span style={{ fontFamily: C.mono, color: C.brass, fontWeight: 600 }}>{totalRows}</span> total row{totalRows !== 1 ? 's' : ''} across {batchCombos.filter(b => b.combos.length).length} batch{batchCombos.filter(b => b.combos.length).length !== 1 ? 'es' : ''}</div>}
              {batchErrors.length > 0 && (
                <div style={{ background: `${C.danger}18`, border: `1px solid ${C.danger}44`, borderRadius: 8, padding: '8px 12px', marginBottom: 8, fontSize: 11, color: C.danger }}>
                  <strong>Required fields missing:</strong>
                  {batchErrors.map(({ bi, names }) => (
                    <div key={bi} style={{ marginTop: 3 }}>Batch {bi + 1}: {names.join(', ')}</div>
                  ))}
                </div>
              )}
              <button onClick={handleGenerate} disabled={totalRows === 0 || batchErrors.length > 0} style={{ ...btn('primary'), width: '100%', padding: '11px 0', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, opacity: (totalRows === 0 || batchErrors.length > 0) ? 0.4 : 1 }}>
                ⚡ Generate {totalRows > 0 ? `${totalRows} ` : ''}rows →
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Import from a spreadsheet ─────────────────────────────────────────────────
//
// The people using this tool keep their campaigns in Excel today, so the clipboard is not the
// only way in. Anything SheetJS reads is accepted; the header row is matched to the level's
// fields by name, and the mapping is shown and editable before a single row is written.

/** "Ad Set Name" and "ad_set_name" are the same column as far as matching is concerned. */
const normHeader = (s) => String(s ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

function autoMap(headers, fields) {
  const byNorm = new Map();
  fields.forEach((f, fi) => {
    byNorm.set(normHeader(f.name), fi);
    if (f.field_key) byNorm.set(normHeader(f.field_key), fi);
  });
  const taken = new Set();
  return headers.map(h => {
    const fi = byNorm.get(normHeader(h));
    if (fi === undefined || taken.has(fi)) return -1;   // -1 = skip this column
    taken.add(fi);
    return fi;
  });
}

function ImportModal({ fields, dictValues, optionsByField, onImport, onClose }) {
  const [state, setState] = useState({ step: 'pick' }); // pick | mapping | error
  const [busy,  setBusy]  = useState(false);
  const [mode,  setMode]  = useState('append');
  const [hasHeader, setHasHeader] = useState(true);
  const [mapping, setMapping] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef();

  const readFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      // Loaded on demand: SheetJS is ~400KB and only the import path needs it, so it stays
      // out of the bundle every other page pays for.
      const XLSX = await import('xlsx');
      // A real workbook stores a date as a number with a format, so SheetJS can hand back an
      // unambiguous Date. A CSV stores "05/01/2024" as text, and SheetJS guesses month-first
      // at it — turning 5 January into 1 May with nothing on screen to say so. So text files
      // are read as text and left to `normaliseDate`, which reads day-first like the rest of
      // this tool. Both paths use raw values; nothing is re-rendered through a display format.
      const isText = /\.(csv|tsv|txt)$/i.test(file.name);
      const wb = isText
        ? XLSX.read(await file.text(), { type: 'string', raw: true, cellDates: false })
        : XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: true });
      const sheetName = wb.SheetNames[0];
      const grid = XLSX.utils.sheet_to_json(wb.Sheets[sheetName], {
        header: 1, defval: '', blankrows: false, raw: true,
      });
      if (!grid.length) { setState({ step: 'error', msg: `${file.name} has no rows in "${sheetName}".` }); return; }
      const width   = Math.max(...grid.map(r => r.length));
      const headers = Array.from({ length: width }, (_, i) => cellText(grid[0][i]).trim());
      setMapping(autoMap(headers, fields));
      setState({ step: 'mapping', fileName: file.name, sheetName, sheets: wb.SheetNames, headers, grid });
    } catch (e) {
      setState({ step: 'error', msg: `Could not read ${file.name}: ${e.message}` });
    } finally { setBusy(false); }
  };

  const dataRows = state.step === 'mapping' ? state.grid.slice(hasHeader ? 1 : 0) : [];

  // Row values keyed by field id, with every value put through the same coercion a paste gets.
  const built = useMemo(() => {
    if (state.step !== 'mapping') return [];
    return dataRows.map(r => {
      const values = {};
      mapping.forEach((fi, ci) => {
        if (fi < 0) return;
        const v = coerceForField(fields[fi], r[ci]);
        if (v !== '') values[fields[fi].id] = v;
      });
      return values;
    }).filter(v => Object.keys(v).length > 0);
  }, [state, dataRows, mapping, fields]);

  // What the user needs to know before committing: columns going nowhere, and values that
  // will land as errors. Counted, not just flagged, so "3 of 400" reads differently from
  // "380 of 400" — which usually means the wrong column is mapped.
  const issues = useMemo(() => {
    if (state.step !== 'mapping') return null;
    const skipped = state.headers
      .map((h, ci) => (mapping[ci] < 0 && (h || dataRows.some(r => cellText(r[ci]).trim())) ? (h || `Column ${ci + 1}`) : null))
      .filter(Boolean);
    const badValues = {};
    built.forEach(values => {
      fields.forEach((f, fi) => {
        const raw = values[f.id];
        if (raw === undefined) return;
        if (validateField(f, raw, optionsByField[fi])) {
          (badValues[f.name] ||= new Set()).add(raw);
        }
      });
    });
    return { skipped, badValues };
  }, [state, mapping, built, fields, optionsByField, dataRows]);

  const unmappedFields = state.step === 'mapping'
    ? fields.filter((_, fi) => !mapping.includes(fi)).map(f => f.name)
    : [];

  const cell = { fontSize: 11, padding: '4px 8px', borderBottom: `1px solid ${C.borderSoft}`, textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 };

  return (
    <div className="o-scrim" style={{ zIndex: 1000 }} onClick={() => !busy && onClose()}>
      <div className="o-modal" style={{ maxWidth: 880, maxHeight: '88vh' }} onClick={e => e.stopPropagation()}>
        <div className="o-modal-head">
          <h2>Import from a spreadsheet</h2>
        </div>

        <div className="o-modal-body" style={{ overflowY: 'auto' }}>
          {state.step === 'error' && (
            <div className="o-state o-error" style={{ marginBottom: 12 }}>{state.msg}</div>
          )}

          {state.step !== 'mapping' && (
            <>
              <div
                className="o-drop"
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); readFile(e.dataTransfer.files?.[0]); }}
                onClick={() => fileRef.current?.click()}
                style={{
                  cursor: 'pointer', textAlign: 'center', padding: '38px 20px',
                  border: `1px dashed ${dragOver ? C.brass : C.border}`, borderRadius: T.rMd,
                  background: dragOver ? C.brassWash : C.s2,
                }}
              >
                <div style={{ fontSize: 13, color: C.text, fontWeight: 600, marginBottom: 4 }}>
                  {busy ? 'Reading…' : 'Drop a file here, or click to choose one'}
                </div>
                <div style={{ fontSize: 12, color: C.muted }}>.xlsx, .xls, .csv or .tsv — the first sheet is read</div>
              </div>
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv,.tsv,.txt" style={{ display: 'none' }}
                onChange={e => { readFile(e.target.files?.[0]); e.target.value = ''; }} />
            </>
          )}

          {state.step === 'mapping' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
                <span style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>{state.fileName}</span>
                <span className="o-badge o-grey">{state.sheetName}</span>
                <span style={{ fontSize: 12, color: C.muted }}>
                  {built.length} row{built.length !== 1 ? 's' : ''} ready
                </span>
                <div style={{ flex: 1 }} />
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.dim, cursor: 'pointer' }}>
                  <input type="checkbox" checked={hasHeader} onChange={e => setHasHeader(e.target.checked)} style={{ accentColor: C.ink }} />
                  First row is a header
                </label>
              </div>

              <div style={{ ...label(), marginBottom: 6 }}>Column mapping</div>
              <div style={{ border: `1px solid ${C.border}`, borderRadius: T.rMd, overflowX: 'auto', marginBottom: 16 }}>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <thead>
                    <tr>
                      {state.headers.map((h, ci) => (
                        <th key={ci} style={{ ...cell, background: C.s2, borderBottom: `1px solid ${C.border}`, minWidth: 130 }}>
                          <div style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {hasHeader ? (h || `Column ${ci + 1}`) : `Column ${ci + 1}`}
                          </div>
                          <select
                            value={mapping[ci]}
                            onChange={e => setMapping(m => m.map((v, i) => {
                              const picked = Number(e.target.value);
                              // A field can only take one column, so choosing it here releases
                              // it wherever it was mapped before.
                              if (i === ci) return picked;
                              return picked >= 0 && v === picked ? -1 : v;
                            }))}
                            style={{ width: '100%', background: C.surface, border: `1px solid ${mapping[ci] < 0 ? C.border : C.brass + '66'}`, borderRadius: T.rSm, padding: '3px 5px', fontSize: 11, fontFamily: 'inherit', color: mapping[ci] < 0 ? C.muted : C.text, outline: 'none' }}
                          >
                            <option value={-1}>— skip —</option>
                            {fields.map((f, fi) => <option key={f.id} value={fi}>{f.name}</option>)}
                          </select>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {dataRows.slice(0, 5).map((r, i) => (
                      <tr key={i}>
                        {state.headers.map((_, ci) => (
                          <td key={ci} style={{ ...cell, color: mapping[ci] < 0 ? C.muted : C.text }}>
                            {cellText(r[ci])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {(issues.skipped.length > 0 || unmappedFields.length > 0 || Object.keys(issues.badValues).length > 0) && (
                <div className="o-hint" style={{ marginBottom: 16, fontSize: 12, lineHeight: 1.6 }}>
                  {issues.skipped.length > 0 && (
                    <div><b>Not imported:</b> {issues.skipped.join(', ')} — nothing in {issues.skipped.length === 1 ? 'that column' : 'those columns'} is mapped to a field.</div>
                  )}
                  {unmappedFields.length > 0 && (
                    <div><b>Left empty:</b> {unmappedFields.join(', ')} — the file has no column for {unmappedFields.length === 1 ? 'it' : 'them'}.</div>
                  )}
                  {Object.entries(issues.badValues).map(([name, vals]) => (
                    <div key={name}>
                      <b>{name}:</b> {vals.size} value{vals.size !== 1 ? 's' : ''} will import as {vals.size === 1 ? 'an error' : 'errors'} — {[...vals].slice(0, 3).map(v => `"${v}"`).join(', ')}{vals.size > 3 ? '…' : ''}
                    </div>
                  ))}
                </div>
              )}

              <div style={{ ...label(), marginBottom: 6 }}>Where the rows go</div>
              <div style={{ display: 'flex', gap: 16 }}>
                {[
                  { id: 'append',  title: 'Add to the sheet',  note: 'Existing rows are kept; these are added after them.' },
                  { id: 'replace', title: 'Replace the sheet', note: 'Every row currently in this level is discarded first.' },
                ].map(o => (
                  <label key={o.id} style={{ flex: 1, display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer', padding: '10px 12px', border: `1px solid ${mode === o.id ? C.ink : C.border}`, borderRadius: T.rMd, background: mode === o.id ? C.s2 : 'transparent' }}>
                    <input type="radio" name="import-mode" checked={mode === o.id} onChange={() => setMode(o.id)} style={{ accentColor: C.ink, marginTop: 2 }} />
                    <span>
                      <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.text }}>{o.title}</span>
                      <span style={{ display: 'block', fontSize: 11, color: C.muted }}>{o.note}</span>
                    </span>
                  </label>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="o-modal-foot">
          {state.step === 'mapping' && (
            <button className="o-btn" style={{ marginRight: 'auto' }} onClick={() => setState({ step: 'pick' })}>
              Choose another file
            </button>
          )}
          <button className="o-btn" onClick={onClose} disabled={busy}>Cancel</button>
          {state.step === 'mapping' && (
            <button className="o-btn o-btn-primary" disabled={!built.length}
              onClick={() => { onImport(built, mode); onClose(); }}>
              Import {built.length} row{built.length !== 1 ? 's' : ''}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Optional adapter reuses spreadsheet interactions with another module-local save/output
// contract. Without it, campaign taxonomy persistence and blank-row seeding are unchanged.
export default function SpreadsheetGrid({ taxonomy, dictValues, rows, setRows, clientId, platformId, levelName, readOnly, campaignName, activeGroupId, onGroupCreated, onSaved, nameBlocked, historyRef, historyKey = 'default', adapter, errorDetails, allowBatchGenerate = true }) {
  const fields = taxonomy?.fields || [];
  const outputLabel = adapter?.outputLabel || 'Taxonomy string';

  // Column index model. `fi` indexes `fields`, so every writer can keep saying `fields[fi]`.
  // The taxonomy string is the leftmost column and is computed, not stored, so it sits at -1:
  // it orders correctly against the fields (left of all of them, which is where it renders)
  // without shifting a single field index. Anything that writes cells clamps f1 to 0 —
  // there is no field behind -1.
  const TAX_COL = -1;
  const LAST_FI = fields.length - 1;

  const [sel,           setSel]           = useState(null);
  const [selEnd,        setSelEnd]        = useState(null);
  const [editCell,      setEditCell]      = useState(null);
  const [saveMsg,       setSaveMsg]       = useState(null);       // transient messages (Copied!, blocked, etc.)
  const [saveStatus,    setSaveStatus]    = useState({ state: 'idle' }); // idle | saving | saved | retrying | error
  const [addCount,      setAddCount]      = useState(5);
  const saving = saveStatus.state === 'saving' || saveStatus.state === 'retrying';
  const [flashSet,      setFlashSet]      = useState(new Set());
  const [ctxMenu,       setCtxMenu]       = useState(null);
  const [ctxPos,        setCtxPos]        = useState(null);
  const ctxMenuRef                        = useRef(null);
  const [hoverRow,      setHoverRow]      = useState(null);
  // True once the pointer has moved with the button down: a click that turns into a drag is a
  // selection, not a request to open a dictionary cell's list.
  const dragMovedRef                      = useRef(false);
  // First character typed into a dictionary cell, handed to the editor as its opening query so
  // that typing straight into the cell searches, exactly as typing into a text cell replaces.
  const pendingQueryRef                   = useRef('');
  const [showBatch,     setShowBatch]     = useState(false);
  const [selRows,       setSelRows]       = useState(new Set());
  const [showErrDetails, setShowErrDetails] = useState(false);
  const [activeFilters,    setActiveFilters]    = useState({});   // { colKey: filter spec | null }
  const [openFilter,       setOpenFilter]       = useState(null); // colKey of open dropdown
  const [filterRect,       setFilterRect]       = useState(null); // DOMRect of filter button
  const [sortSpec,         setSortSpec]         = useState([]);   // [{ colKey, dir }] — display order only, first is primary
  const [colWidths,        setColWidths]        = useState({});   // { colKey: px } from the resize handles
  const [find,             setFind]             = useState(null); // { q, replace, matchCase, msg } while the panel is open
  const [fillTo,           setFillTo]           = useState(null); // view target of the fill handle: { pos, fi }
  const [showImport,       setShowImport]       = useState(false);
  const [filtersOn,        setFiltersOn]        = useState(true);  // Ctrl+Shift+L shows/hides the header filter controls

  const tableRef      = useRef();
  const fillFrom      = useRef(null); // range snapshot captured when the fill handle is grabbed
  const inputRefs     = useRef({});
  const isDragging    = useRef(false);
  const lastRowAnchor = useRef(null);
  const scrollRef     = useRef();
  const [scrollTop,  setScrollTop] = useState(0);

  // ── Autosave + Undo/Redo ────────────────────────────────────────────────────
  const editingSnapshotRef = useRef(null); // rows array captured when the current cell-edit session began
  // The stacks live on the page, not in this component: switching level tab or channel
  // remounts the grid (its `key` changes), and history kept here was wiped every time — you
  // could not undo your way back across a tab switch. Per level, because an undo has to
  // restore the sheet it was recorded against.
  const localHistory = useRef({});
  const hist = useMemo(() => {
    const store = historyRef?.current ?? localHistory.current;
    return (store[historyKey] ||= { undo: [], redo: [] });
  }, [historyRef, historyKey]);
  const saveTimerRef = useRef(null);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  // Tracks the name last confirmed synced to the backend, so autosave only PATCHes the group's
  // name when it actually changed (not on every debounce tick). Seeded from the current name when
  // a group already exists (editing an existing campaign) so the first autosave isn't a no-op PATCH.
  const lastSyncedNameRef = useRef(activeGroupId ? (campaignName || '').trim() : null);

  const ROW_H  = 36;
  const BUFFER = 15;

  // The grid takes the height that is actually left on screen rather than a fixed 600px, so a
  // tall monitor shows the rows it has room for. Clamped so it never collapses to nothing on a
  // short window, and re-measured on resize because the virtualiser's maths depend on it.
  const [vH, setVH] = useState(() => Math.max(320, Math.round((window.innerHeight || 900) - 380)));
  useEffect(() => {
    const onResize = () => setVH(Math.max(320, Math.round(window.innerHeight - 380)));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const V_H = vH;

  // colKey → field id. Declared before every memo that reads a cell by column key, so
  // `valueAt` is never called into a temporal dead zone during render.
  const fieldIdByKey = useMemo(() => {
    const m = {};
    fields.forEach(f => { m[String(f.id)] = f.id; });
    return m;
  }, [fields]);

  // ── Per-row taxonomy string ───────────────────────────────────────────────
  //
  // Built once per rows change and read everywhere else — filtering, sorting, the filter
  // value lists, the save filter, the counters and the cells themselves. It used to be
  // rebuilt for every row inside four separate memos plus the render, so a single keystroke
  // in a 5,000-row sheet re-derived 20,000 strings.
  //
  // Keyed by row identity, not index: `setCell` replaces only the row it writes to, so on a
  // keystroke 499 of 500 rows are the same object as last render and their string is reused
  // rather than rebuilt. The reuse also keeps each string referentially stable, which is what
  // lets a memoized row skip re-rendering at all.
  const taxCacheRef = useRef(new Map());
  useMemo(() => { taxCacheRef.current = new Map(); }, [taxonomy, dictValues, adapter?.output]);
  const taxStrings = useMemo(() => {
    const prev = taxCacheRef.current, next = new Map();
    const out = rows.map(r => {
      let s = prev.get(r);
      if (s === undefined) s = adapter ? adapter.output(r) : buildTaxString(taxonomy, r.values, dictValues);
      next.set(r, s);
      return s;
    });
    taxCacheRef.current = next;
    return out;
  }, [rows, taxonomy, dictValues, adapter?.output]);
  const taxStringsRef = useRef(taxStrings);
  taxStringsRef.current = taxStrings;

  const valueAt = useCallback(
    (ri, colKey) => colKey === '__tax__'
      ? (taxStrings[ri] || '')
      : String(rows[ri]?.values[fieldIdByKey[colKey]] ?? ''),
    [rows, taxStrings, fieldIdByKey],
  );

  // ── Filtered row indices ──────────────────────────────────────────────────
  //
  // Rows that matched when the filter was applied stay on screen even after an edit stops
  // them matching. Re-evaluating live means a row vanishes from under the cursor the moment
  // you type into the filtered column — taking the keystroke with it. Excel re-filters only
  // when you re-apply, and so does this: `pinnedIds` freezes the set at apply time.
  const matchIndices = useMemo(() => {
    const active = Object.entries(activeFilters).filter(([, s]) => s != null);
    if (!active.length) return rows.map((_, i) => i);
    return rows.reduce((acc, _row, i) => {
      for (const [colKey, spec] of active) {
        if (!matchesFilter(valueAt(i, colKey), spec)) return acc;
      }
      acc.push(i);
      return acc;
    }, []);
  }, [rows, activeFilters, valueAt]);

  const hasActiveFilters = Object.values(activeFilters).some(s => s != null);

  // Recomputed only when the filter itself changes — never on a cell edit.
  const [pinnedIds, setPinnedIds] = useState(null);
  const matchRef = useRef(matchIndices);
  matchRef.current = matchIndices;
  useEffect(() => {
    if (!hasActiveFilters) { setPinnedIds(null); return; }
    setPinnedIds(new Set(matchRef.current.map(i => rowsRef.current[i]?.id).filter(Boolean)));
  }, [activeFilters, hasActiveFilters]);

  const filteredIndices = useMemo(() => {
    if (!hasActiveFilters || !pinnedIds) return matchIndices;
    return rows.reduce((acc, r, i) => { if (pinnedIds.has(r.id)) acc.push(i); return acc; }, []);
  }, [hasActiveFilters, pinnedIds, matchIndices, rows]);

  // True once edits have made the frozen set disagree with the filter — the only time
  // offering "reapply" means anything.
  const staleFilter = hasActiveFilters && pinnedIds != null
    && (matchIndices.length !== filteredIndices.length
        || matchIndices.some((ri, i) => ri !== filteredIndices[i]));

  const reapplyFilters = useCallback(() => {
    setPinnedIds(new Set(matchRef.current.map(i => rowsRef.current[i]?.id).filter(Boolean)));
  }, []);

  /** A row worth persisting: the user has put something in it, complete or not. */
  const rowHasContent = useCallback(
    (row) => fields.some(f => String(row.values[f.id] ?? '').trim() !== ''),
    [fields],
  );

  // ── Display order ─────────────────────────────────────────────────────────
  //
  // `rows` is storage; this is what the user sees. Filtering hides rows and sorting reorders
  // them, so a row's index in `rows` stops matching its place on screen. Everything the user
  // drives — arrows, Tab, copy, paste, delete, row selection — works in THIS space and
  // converts back at the edges. Navigating in `rows` space is what let the cursor walk into
  // a filtered-out row and edit records nobody could see.
  //
  // `sortSpec` is a list, applied left to right: the first column decides, later ones break
  // its ties. Sheet order breaks the last tie, so a sort never shuffles equal rows against
  // each other.
  const viewIndices = useMemo(() => {
    if (!sortSpec.length) return filteredIndices;
    return [...filteredIndices].sort((a, b) => {
      for (const { colKey, dir } of sortSpec) {
        const c = compareValues(valueAt(a, colKey), valueAt(b, colKey), dir === 'desc' ? -1 : 1);
        if (c) return c;
      }
      return a - b;
    });
  }, [filteredIndices, sortSpec, valueAt]);

  const posByRow = useMemo(() => {
    const m = new Map();
    viewIndices.forEach((ri, p) => m.set(ri, p));
    return m;
  }, [viewIndices]);

  /** Screen position of a stored row, or -1 when the row is filtered out. */
  const posOf = useCallback((ri) => {
    const p = posByRow.get(ri);
    return p === undefined ? -1 : p;
  }, [posByRow]);

  /** Stored row index at a screen position, clamped to the visible range. */
  const riAt = useCallback((p) => viewIndices[Math.max(0, Math.min(viewIndices.length - 1, p))],
    [viewIndices]);

  // Appending a row only makes sense when every row is on screen: a new blank row under an
  // active filter (or a sort) would be created and instantly hidden, which reads as nothing
  // happening. Excel refuses the same way.
  const canGrow = !hasActiveFilters && !sortSpec.length;

  // Allowed values per field, resolved once rather than per cell per keystroke.
  const optionsByField = useMemo(
    () => fields.map(f => fieldOptions(f, dictValues)),
    [fields, dictValues],
  );

  // Live validation. Cached by row identity for the same two reasons as `taxStrings`: one
  // keystroke revalidates one row, and an untouched row keeps the very same error object, so
  // the row that renders it can be skipped entirely.
  const errCacheRef = useRef(new Map());
  useMemo(() => { errCacheRef.current = new Map(); }, [fields, optionsByField, adapter?.fieldError, adapter?.rowError]);
  const validationErrors = useMemo(() => {
    const prev = errCacheRef.current, next = new Map(), errs = {};
    rows.forEach((row, ri) => {
      let rowErrs = prev.get(row);
      if (rowErrs === undefined) {
        const rowError = adapter?.rowError?.(row);
        rowErrs = rowError ? { '-1': rowError } : null;
        if (adapter || fields.some(f => (row.values[f.id] ?? '') !== '')) {
          fields.forEach((f, fi) => {
            const err = adapter ? adapter.fieldError(row, f) : validateField(f, row.values[f.id], optionsByField[fi]);
            if (err) { if (!rowErrs) rowErrs = {}; rowErrs[fi] = err; }
          });
        }
      }
      next.set(row, rowErrs);
      if (rowErrs) errs[ri] = rowErrs;
    });
    errCacheRef.current = next;
    return errs;
  }, [rows, fields, optionsByField, adapter?.fieldError, adapter?.rowError]);

  // An empty sheet opens with rows ready to type into, the way a spreadsheet does. They hold
  // no values, so `rowHasContent` keeps them out of every save — nothing is created by
  // opening a level and walking away. Not recorded as an undo step for the same reason.
  const seededRef = useRef(false);
  useEffect(() => {
    if (adapter || readOnly || seededRef.current || !fields.length || rows.length) return;
    seededRef.current = true;
    setRows(prev => prev.length ? prev : Array.from({ length: 10 }, () => mkRow()));
  }, [readOnly, fields.length, rows.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset scroll to top when filters change
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    setScrollTop(0);
  }, [activeFilters]);

  // A wheel or trackpad emits scroll events faster than the browser paints, and each one used
  // to queue its own render of the whole body. Coalescing to one per frame is the difference
  // between a sheet that glides and one that stutters; the value read is always the latest.
  const scrollRafRef = useRef(0);
  const onScroll = useCallback((e) => {
    const el = e.currentTarget;
    if (scrollRafRef.current) return;
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = 0;
      setScrollTop(el.scrollTop);
    });
  }, []);
  useEffect(() => () => cancelAnimationFrame(scrollRafRef.current), []);

  const visStart  = useMemo(() => Math.max(0, Math.floor(scrollTop / ROW_H) - BUFFER), [scrollTop]);
  const visEnd    = useMemo(() => Math.min(viewIndices.length, Math.ceil((scrollTop + V_H) / ROW_H) + BUFFER), [scrollTop, viewIndices.length]);
  const padTop    = visStart * ROW_H;
  const padBottom = Math.max(0, (viewIndices.length - visEnd) * ROW_H);
  const colSpan   = 2 + fields.length;

  useEffect(() => {
    const up = () => { isDragging.current = false; };
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, []);

  useEffect(() => {
    if (!ctxMenu) return;
    const close = (e) => { if (!e.target.closest?.('.sg-ctx-menu')) setCtxMenu(null); };
    const esc   = (e) => { if (e.key === 'Escape') setCtxMenu(null); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [ctxMenu]);

  // A right-click near the bottom (or right) edge would otherwise open the menu off-screen —
  // it is position:fixed at the cursor, so nothing clips or scrolls it back into view. Measure
  // once it exists and flip it above/left of the cursor, then clamp to the viewport.
  useLayoutEffect(() => {
    if (!ctxMenu) { setCtxPos(null); return; }
    const el = ctxMenuRef.current;
    if (!el) return;
    const M = 8;
    const { width, height } = el.getBoundingClientRect();
    const vw = window.innerWidth, vh = window.innerHeight;
    let top  = ctxMenu.y;
    let left = ctxMenu.x;
    if (top + height > vh - M)  top  = ctxMenu.y - height;   // flip above the cursor
    if (left + width > vw - M)  left = ctxMenu.x - width;    // flip left of the cursor
    setCtxPos({
      top:  Math.max(M, Math.min(top,  vh - height - M)),
      left: Math.max(M, Math.min(left, vw - width  - M)),
    });
  }, [ctxMenu]);

  const mkRow = (base = {}) => {
    const values = { ...base };
    fields.forEach(f => {
      if (f.dict_list_id && (values[f.id] === undefined || values[f.id] === '')) {
        const opts = dictValues[f.dict_list_id] || [];
        if (opts.length === 1) values[f.id] = opts[0].value;
      }
    });
    return { id: adapter ? adapter.createRowId() : uid(), values };
  };
  // Adding rows is an undoable step like any other — without the snapshot, Ctrl+Z after
  // "+5 rows" silently reverted whatever the user did before that instead.
  const addRows = (n, base = {}) => setRows(prev => {
    pushUndo(prev);
    return [...prev, ...Array.from({ length: n }, () => mkRow(base))];
  });

  // Range bounds are screen positions (p1..p2), not stored indices, so a range never spans
  // rows the filter is hiding.
  const getRange = useCallback(() => {
    if (!sel) return { p1: 0, p2: -1, f1: 0, f2: -1 };
    const end = selEnd || sel;
    const pa = posOf(sel.ri), pb = posOf(end.ri);
    if (pa < 0 || pb < 0) return { p1: 0, p2: -1, f1: 0, f2: -1 };
    return {
      p1: Math.min(pa, pb), p2: Math.max(pa, pb),
      f1: Math.min(sel.fi, end.fi), f2: Math.max(sel.fi, end.fi),
    };
  }, [sel, selEnd, posOf]);

  /** Stored row indices covered by the selection, in the order they appear on screen. */
  const rangeRows = useCallback(() => {
    const { p1, p2 } = getRange();
    return p2 < p1 ? [] : viewIndices.slice(p1, p2 + 1);
  }, [getRange, viewIndices]);

  const inRange  = (ri, fi) => {
    if (!sel) return false;
    const { p1, p2, f1, f2 } = getRange();
    const p = posOf(ri);
    return p >= p1 && p <= p2 && fi >= f1 && fi <= f2;
  };
  const isAnchor = (ri, fi) => sel && ri === sel.ri && fi === sel.fi;

  // The cell the fill handle hangs off: bottom-right of the selection.
  const rangeCorner = useCallback(() => {
    const { p2, f2 } = getRange();
    // f2 < 0 means the selection is the taxonomy column alone — nothing writable to fill from.
    if (p2 < 0 || f2 < 0) return null;
    return { ri: viewIndices[p2], fi: Math.min(f2, fields.length - 1) };
  }, [getRange, viewIndices, fields.length]);

  /**
   * The block the fill would write, given where the handle currently sits. Dragging past the
   * bottom or the right edge of the source extends that way; dragging above or left of it
   * fills backwards, which Excel allows and this grid used to ignore entirely.
   */
  const fillTarget = useCallback(() => {
    const src = fillFrom.current;
    if (!src || !fillTo) return null;
    const lastFi = Math.min(src.f2, fields.length - 1);
    const dRow = fillTo.pos > src.p2 ? fillTo.pos - src.p2 : fillTo.pos < src.p1 ? fillTo.pos - src.p1 : 0;
    const dCol = fillTo.fi  > lastFi  ? fillTo.fi  - lastFi  : fillTo.fi  < src.f1 ? fillTo.fi  - src.f1  : 0;
    // One axis at a time, as in Excel: whichever the pointer has travelled further along.
    if (!dRow && !dCol) return null;
    if (Math.abs(dRow) >= Math.abs(dCol)) {
      return dRow > 0
        ? { p1: src.p2 + 1, p2: fillTo.pos, f1: src.f1, f2: lastFi, axis: 'row', dir: 1 }
        : { p1: fillTo.pos, p2: src.p1 - 1, f1: src.f1, f2: lastFi, axis: 'row', dir: -1 };
    }
    return dCol > 0
      ? { p1: src.p1, p2: src.p2, f1: lastFi + 1, f2: fillTo.fi, axis: 'col', dir: 1 }
      : { p1: src.p1, p2: src.p2, f1: Math.max(0, fillTo.fi), f2: src.f1 - 1, axis: 'col', dir: -1 };
  }, [fillTo, fields.length]);

  const inFillPreview = (ri, fi) => {
    const t = fillTarget();
    if (!t) return false;
    const p = posOf(ri);
    return p >= t.p1 && p <= t.p2 && fi >= t.f1 && fi <= t.f2;
  };

  const setCell = (ri, fi, val) => {
    setRows(prev => prev.map((r, i) => i === ri ? { ...r, values: { ...r.values, [fields[fi].id]: val } } : r));
    scheduleAutosave(false); // debounced ~1.5s after last keystroke; commitEdit() forces immediate save on blur/nav
  };

  const flash = (keys) => { setFlashSet(new Set(keys)); setTimeout(() => setFlashSet(new Set()), 500); };

  const pendingAutosaveRef = useRef(false); // true while a debounced (not-yet-fired) save is queued

  const pushUndo = useCallback((prevRows) => {
    hist.undo.push(prevRows);
    if (hist.undo.length > 100) hist.undo.shift();
    hist.redo.length = 0;
  }, [hist]);

  // Per-row error count at save time — independent of the display-only `validationErrors` map so it
  // stays correct even though `toSave` filters/reindexes rows (avoids fragile index matching).
  const countRowErrors = useCallback((row) =>
    fields.reduce((acc, f, fi) => acc + (validateField(f, row.values[f.id], optionsByField[fi]) ? 1 : 0), 0),
  [fields, optionsByField]);

  // Signature of the last payload confirmed saved. Typing in one cell fires the 1.5s debounce and
  // then commitEdit()'s immediate save on blur/arrow-key, so the same rows were POSTed twice —
  // each POST writing an activity-log row. Identical payloads are now skipped, which keeps the
  // audit trail readable without weakening the "never lose work" behaviour: anything that actually
  // changed produces a different signature and still saves.
  const lastSavedSigRef = useRef(null);

  const doAutosave = useCallback(async () => {
    if (readOnly) return;
    if (adapter) {
      setSaveStatus({ state: 'saving' });
      try { await adapter.save(); setSaveStatus({ state: 'saved' }); }
      catch (e) { setSaveStatus({ state: 'error', msg: e.message }); }
      return;
    }
    // An unnamed campaign saves as a draft rather than not at all. The group is keyed by its
    // name, so one has to exist before rows can be written; the server generates it and flags
    // the campaign a draft, and naming it later is a rename that clears the flag. Refusing to
    // save until the name field was filled in meant a whole sheet could be typed and lost to a
    // refresh, a crash or simply navigating away.
    const unnamed = !campaignName?.trim();
    if (unnamed && !activeGroupId && !rowsRef.current.some(rowHasContent)) return;
    // A name that collides is still refused — writing the rows under someone else's campaign
    // name is worse than holding them here, and the name is the one thing the user can fix.
    if (!unnamed && nameBlocked) { setSaveStatus({ state: 'error', msg: 'Campaign name already exists' }); return; }
    // Validation errors no longer block saving — data is never lost to a refresh or expired session.
    // The error badge/details panel still shows live, and each row's error count is persisted alongside it.

    // Any row the user has put something into is saved, complete or not. Filtering to rows
    // that build a full taxonomy string meant a half-filled row was silently dropped and came
    // back missing after a reload — losing work the user could see on screen.
    const toSave = rowsRef.current.filter(rowHasContent);
    if (!toSave.length && !activeGroupId) return;

    const payload = toSave.map(r => ({ field_values: r.values, error_count: countRowErrors(r) }));
    const sig = JSON.stringify([(campaignName || '').trim(), levelName, platformId, payload]);
    if (activeGroupId && sig === lastSavedSigRef.current) { setSaveStatus({ state: 'saved' }); return; }

    setSaveStatus({ state: 'saving' });

    const attempt = async (retriesLeft, backoff) => {
      try {
        let gid = activeGroupId;
        const trimmedName = (campaignName || '').trim();
        if (!gid) {
          const gd = trimmedName
            ? await txApi.createCampaignGroup({ name: trimmedName, client_id: clientId, platform_id: platformId })
            : await txApi.createDraftCampaignGroup({ client_id: clientId, platform_id: platformId });
          gid = gd.group.id;
          lastSyncedNameRef.current = gd.group.name;
          // The page takes the generated name so the field shows what the campaign is called;
          // editing it from there is the rename that turns the draft into a named campaign.
          onGroupCreated(gid, gd.group.name);
        } else if (trimmedName && trimmedName !== lastSyncedNameRef.current) {
          await txApi.updateCampaignGroup(gid, { name: trimmedName });
          lastSyncedNameRef.current = trimmedName;
        }
        await txApi.saveGroupLevel(gid, levelName, platformId, payload);
        lastSavedSigRef.current = sig;
        setSaveStatus({ state: 'saved' });
        onSaved?.();
      } catch (e) {
        if (retriesLeft > 0) {
          setSaveStatus({ state: 'retrying' });
          await new Promise(r => setTimeout(r, backoff));
          return attempt(retriesLeft - 1, backoff * 2);
        }
        setSaveStatus({ state: 'error', msg: e.message });
      }
    };
    await attempt(3, 1500);
  }, [readOnly, campaignName, nameBlocked, activeGroupId, clientId, platformId, levelName, onGroupCreated, onSaved, countRowErrors, rowHasContent, adapter?.save]);

  // Always points at the freshest doAutosave closure — used by the unmount-flush effect below,
  // which only runs once and must not call a stale closure with outdated activeGroupId/campaignName/etc.
  const doAutosaveRef = useRef(doAutosave);
  useEffect(() => { doAutosaveRef.current = doAutosave; }, [doAutosave]);

  /**
   * Queue a save. `immediate` means "as soon as this change is on screen", not "right now".
   *
   * That distinction is the whole point. `doAutosave` reads `rowsRef`, which is assigned during
   * render, so calling it synchronously from an event handler reads the rows as they were
   * BEFORE the `setRows` in that same handler. Every immediate-save caller does exactly that —
   * paste, fill down/right, delete, undo, redo — so each of them posted the previous state and
   * then reported "All changes saved". The paste itself was only written later, if some further
   * edit happened to trigger another save; leaving the page first lost it.
   *
   * A zero timeout is enough: React commits the state update before the next macrotask, so the
   * ref is current by the time this runs. It stays marked pending until it fires, so navigating
   * away in between still flushes it through the unmount effect below.
   */
  const scheduleAutosave = useCallback((immediate = false) => {
    clearTimeout(saveTimerRef.current);
    pendingAutosaveRef.current = true;
    saveTimerRef.current = setTimeout(() => {
      pendingAutosaveRef.current = false;
      doAutosave();
    }, immediate ? 0 : 1500);
  }, [doAutosave]);

  // If the user navigates away (or the level/platform switches, remounting this component) while a
  // debounced keystroke-save is still pending, flush it immediately instead of silently dropping it.
  useEffect(() => () => {
    clearTimeout(saveTimerRef.current);
    if (pendingAutosaveRef.current) {
      pendingAutosaveRef.current = false;
      doAutosaveRef.current();
    }
  }, []);

  // Commit an in-progress cell edit: if the value actually changed, record one undo step and save immediately
  const commitEdit = useCallback(() => {
    const snap = editingSnapshotRef.current;
    editingSnapshotRef.current = null;
    if (snap && snap !== rowsRef.current) {
      pushUndo(snap);
      scheduleAutosave(true);
    }
  }, [pushUndo, scheduleAutosave]);

  // Cancel an in-progress cell edit and revert to the pristine snapshot (Excel-style Escape)
  const cancelEdit = useCallback(() => {
    const snap = editingSnapshotRef.current;
    editingSnapshotRef.current = null;
    if (snap) setRows(snap);
  }, [setRows]);

  const doUndo = useCallback(() => {
    if (readOnly) return;
    const prev = hist.undo.pop();
    if (!prev) return;
    hist.redo.push(rowsRef.current);
    setRows(prev);
    scheduleAutosave(true);
  }, [readOnly, setRows, scheduleAutosave, hist]);

  const doRedo = useCallback(() => {
    if (readOnly) return;
    const next = hist.redo.pop();
    if (!next) return;
    hist.undo.push(rowsRef.current);
    setRows(next);
    scheduleAutosave(true);
  }, [readOnly, setRows, scheduleAutosave, hist]);

  const navigate = useCallback((ri, fi, dir) => {
    commitEdit();
    const cur = posOf(ri);
    let nPos = cur < 0 ? 0 : cur, nfi = fi;
    const lastFi = fields.length - 1;
    if      (dir === 'right') { nfi++; if (nfi > lastFi) { nfi = 0; nPos++; } }
    else if (dir === 'left')  { nfi--; if (nfi < 0) { nfi = lastFi; nPos = Math.max(0, nPos - 1); } }
    else if (dir === 'down')  { nPos++; }
    else if (dir === 'up')    { nPos = Math.max(0, nPos - 1); }

    if (nPos >= viewIndices.length) {
      // Off the bottom. With everything visible this grows the sheet the way Tab/Enter do in
      // Excel; filtered or sorted, it would append a row the user cannot see, so hold instead.
      if (canGrow) {
        addRows(1);
        setSel({ ri: rows.length, fi: nfi }); setSelEnd(null); setEditCell(null);
        setTimeout(() => tableRef.current?.focus(), 0);
        return;
      }
      nPos = viewIndices.length - 1;
    }
    setSel({ ri: riAt(nPos), fi: nfi }); setSelEnd(null); setEditCell(null);
    setTimeout(() => tableRef.current?.focus(), 0);
  }, [fields.length, rows.length, commitEdit, posOf, riAt, viewIndices.length, canGrow]);

  /** Write one value across every cell of the current selection. */
  const setRange = useCallback((valueFor) => {
    const targets = rangeRows();
    const { f1, f2 } = getRange();
    if (!targets.length) return;
    setRows(prev => {
      pushUndo(prev);
      const next = prev.map(r => ({ ...r, values: { ...r.values } }));
      targets.forEach((ri, off) => {
        // Lower clamp: a selection that includes the taxonomy column starts at -1, which has
        // no field behind it. Upper clamp is the same idea from the other end.
        for (let fi = Math.max(0, f1); fi <= Math.min(f2, fields.length - 1); fi++) {
          next[ri].values[fields[fi].id] = valueFor(ri, fi, off);
        }
      });
      return next;
    });
    scheduleAutosave(true);
  }, [rangeRows, getRange, fields, setRows, pushUndo, scheduleAutosave]);

  /** Ctrl+D — repeat the selection's first row down the rest of it. */
  const fillDown = useCallback(() => {
    if (readOnly) return;
    const targets = rangeRows();
    if (targets.length < 2) return;
    const src = rowsRef.current[targets[0]];
    setRange((ri, fi) => src.values[fields[fi].id] ?? '');
  }, [readOnly, rangeRows, setRange, fields]);

  /** Ctrl+R — repeat the selection's first column across the rest of it. */
  const fillRight = useCallback(() => {
    if (readOnly) return;
    const { f1, f2 } = getRange();
    const from = Math.max(0, f1);
    if (Math.min(f2, fields.length - 1) <= from) return;
    setRange((ri) => rowsRef.current[ri].values[fields[from].id] ?? '');
  }, [readOnly, getRange, setRange, fields]);

  /** Ctrl+= — insert as many blank rows as the selection covers, above it. */
  const insertSelRows = useCallback(() => {
    if (readOnly || !canGrow) return;
    const targets = selRows.size ? [...selRows].sort((a, b) => a - b) : rangeRows();
    if (!targets.length) return;
    const at = targets[0], n = targets.length;
    setRows(prev => {
      pushUndo(prev);
      return [...prev.slice(0, at), ...Array.from({ length: n }, () => mkRow()), ...prev.slice(at)];
    });
    scheduleAutosave(false);
  }, [readOnly, canGrow, selRows, rangeRows, setRows, pushUndo, scheduleAutosave]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Ctrl+− (and the context menu) — remove the selected rows outright. */
  const deleteSelRows = useCallback(() => {
    if (readOnly) return;
    const targets = selRows.size ? selRows : new Set(rangeRows());
    if (!targets.size) return;
    setRows(prev => { pushUndo(prev); return prev.filter((_, i) => !targets.has(i)); });
    setSelRows(new Set());
    setSel(null); setSelEnd(null); setEditCell(null);
    lastRowAnchor.current = null;
    scheduleAutosave(true);
  }, [readOnly, selRows, rangeRows, setRows, pushUndo, scheduleAutosave]);

  /** Clear every cell of the selected rows, leaving the rows themselves in place. */
  const clearSelRows = useCallback(() => {
    if (readOnly || !selRows.size) return;
    setRows(prev => {
      pushUndo(prev);
      return prev.map((r, i) => selRows.has(i) ? { ...r, values: {} } : r);
    });
    scheduleAutosave(true);
  }, [readOnly, selRows, setRows, pushUndo, scheduleAutosave]);

  const onKeyDown = useCallback(async (e) => {
    // Excel's row grammar: Delete clears what is in the rows, Ctrl+− removes them, Ctrl+=
    // inserts. Delete used to delete the rows outright, which is destructive on a keystroke
    // people press to blank a cell.
    if (selRows.size > 0 && (e.key === 'Delete' || e.key === 'Backspace') && !editCell) {
      e.preventDefault(); clearSelRows(); return;
    }
    if (!editCell && (e.ctrlKey || e.metaKey) && (e.key === '-' || e.key === '_')) {
      e.preventDefault(); deleteSelRows(); return;
    }
    if (!editCell && (e.ctrlKey || e.metaKey) && (e.key === '=' || e.key === '+')) {
      e.preventDefault(); insertSelRows(); return;
    }
    // Ctrl+Shift+L toggles the filter row wholesale, as in Excel.
    if (!editCell && (e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'l') {
      e.preventDefault();
      setActiveFilters(prev => (Object.values(prev).some(s => s != null) ? {} : prev));
      setFiltersOn(v => !v);
      return;
    }
    // Rows picked by their row number are a selection too. Ctrl+C used to fall through to the
    // `!sel` guard below and copy nothing at all, which is the first thing an Excel user tries.
    if (selRows.size > 0 && (e.ctrlKey || e.metaKey) && !editCell) {
      const k = e.key.toLowerCase();
      if (k === 'c' || k === 'x') {
        e.preventDefault();
        const ordered = viewIndices.filter(ri => selRows.has(ri));
        const tsv = ordered.map(ri => {
          const row = rows[ri];
          const cells = [...fields.map(f => row.values[f.id] || ''), taxStrings[ri] || ''];
          return cells.map(c => /[\t\n"]/.test(c) ? `"${String(c).replace(/"/g, '""')}"` : c).join('\t');
        }).join('\n');
        await writeClipboard(tsv);
        if (k === 'x' && !readOnly) {
          setRows(prev => {
            pushUndo(prev);
            return prev.map((r, i) => selRows.has(i) ? { ...r, values: {} } : r);
          });
          scheduleAutosave(true);
          setSaveMsg('Cut');
        } else setSaveMsg('Copied!');
        setTimeout(() => setSaveMsg(null), 1500);
        return;
      }
    }
    // Shift+Space needs no modifier, so it is handled ahead of the Ctrl block below.
    if (!editCell && e.shiftKey && !e.ctrlKey && !e.metaKey && e.key === ' ' && sel) {
      e.preventDefault();
      setSel({ ri: sel.ri, fi: TAX_COL }); setSelEnd({ ri: sel.ri, fi: LAST_FI });
      return;
    }
    if (!sel) return;
    const { ri, fi } = sel;
    if (e.key === 'Escape') { cancelEdit(); setSel(null); setSelEnd(null); setEditCell(null); setSelRows(new Set()); return; }
    if (e.key === 'Tab') { e.preventDefault(); navigate(editCell?.ri ?? ri, editCell?.fi ?? fi, e.shiftKey ? 'left' : 'right'); return; }
    if (e.key === 'Enter' && !e.shiftKey && !e.altKey) {
      e.preventDefault();
      if (editCell) { navigate(ri, fi, 'down'); }
      else if (!readOnly && fi >= 0) { pendingQueryRef.current = ''; editingSnapshotRef.current = rows; setEditCell({ ri, fi }); setTimeout(() => inputRefs.current[`${ri},${fi}`]?.focus(), 0); }
      else navigate(ri, fi, 'down');   // taxonomy column is computed — nothing to open
      return;
    }
    if (e.key === 'F2' && !editCell) {
      e.preventDefault();
      if (readOnly || fi < 0) return;
      pendingQueryRef.current = '';
      editingSnapshotRef.current = rows;
      setEditCell({ ri, fi });
      setTimeout(() => inputRefs.current[`${ri},${fi}`]?.focus(), 0);
      return;
    }

    const NAV_KEYS = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown'];
    if (!editCell && NAV_KEYS.includes(e.key)) {
      e.preventDefault();
      const cur     = selEnd || sel;
      const curPos  = Math.max(0, posOf(cur.ri));
      const lastPos = viewIndices.length - 1;
      const page    = Math.max(1, Math.floor(V_H / ROW_H) - 1);
      const mod     = e.ctrlKey || e.metaKey;
      let nPos = curPos, nfi = cur.fi;

      switch (e.key) {
        case 'ArrowUp':    nPos = mod ? 0       : Math.max(0, curPos - 1);       break;
        case 'ArrowDown':  nPos = mod ? lastPos : Math.min(lastPos, curPos + 1); break;
        // The taxonomy column is the leftmost one (TAX_COL = -1), so it is where ArrowLeft and
        // Home stop, and the last field is the right-hand end of the row.
        case 'ArrowLeft':  nfi  = mod ? TAX_COL : Math.max(TAX_COL, cur.fi - 1); break;
        case 'ArrowRight': nfi  = mod ? LAST_FI : Math.min(LAST_FI, cur.fi + 1); break;
        // Ctrl+Home/End jump to the corners of the sheet, plain Home/End to the row's ends.
        case 'Home':       nfi = TAX_COL; if (mod) nPos = 0;       break;
        case 'End':        nfi = LAST_FI; if (mod) nPos = lastPos; break;
        case 'PageUp':     nPos = Math.max(0, curPos - page);       break;
        case 'PageDown':   nPos = Math.min(lastPos, curPos + page); break;
        default: break;
      }
      const nri = riAt(nPos);
      if (e.shiftKey) setSelEnd({ ri: nri, fi: nfi });
      else { setSel({ ri: nri, fi: nfi }); setSelEnd(null); }
      // Keep the cursor on screen — virtualization means an off-screen row isn't rendered.
      if (scrollRef.current) {
        const top = nPos * ROW_H, bottom = top + ROW_H;
        const view = scrollRef.current.scrollTop;
        if (top < view) scrollRef.current.scrollTop = top;
        else if (bottom > view + V_H - ROW_H) scrollRef.current.scrollTop = bottom - V_H + ROW_H;
      }
      return;
    }
    if (e.ctrlKey || e.metaKey) {
      const k = e.key.toLowerCase();

      if (k === 'a') {
        e.preventDefault();
        if (!viewIndices.length) return;
        setSel({ ri: viewIndices[0], fi: TAX_COL });
        setSelEnd({ ri: viewIndices[viewIndices.length - 1], fi: LAST_FI });
        setSelRows(new Set());
        return;
      }

      if (k === 'd') { e.preventDefault(); fillDown(); return; }

      if (k === 'r') { e.preventDefault(); fillRight(); return; }

      if (k === 'f') { e.preventDefault(); setFind(f => f || { q: '', replace: '', matchCase: false, msg: null }); return; }

      // Ctrl+Space selects the column, Shift+Space the row.
      if (e.key === ' ') {
        e.preventDefault();
        if (e.shiftKey) { setSel({ ri, fi: TAX_COL }); setSelEnd({ ri, fi: LAST_FI }); }
        else if (viewIndices.length) {
          setSel({ ri: viewIndices[0], fi });
          setSelEnd({ ri: viewIndices[viewIndices.length - 1], fi });
          setEditCell(null);
        }
        return;
      }

      if (k === 'c' || k === 'x') {
        e.preventDefault();
        const { f1, f2 } = getRange();
        const targets = rangeRows();
        const tsv = targets.map(tri => {
          const row = rows[tri];
          const cells = [];
          // Taxonomy string first — copy writes columns in the order they appear on screen.
          if (f1 <= TAX_COL) cells.push(taxStrings[tri] || '');
          for (let ci = Math.max(0, f1); ci <= Math.min(f2, fields.length - 1); ci++) cells.push(row.values[fields[ci].id] || '');
          // Quote the way Excel does, so a value holding a tab or newline survives the round trip.
          return cells.map(c => /[\t\n"]/.test(c) ? `"${String(c).replace(/"/g, '""')}"` : c).join('\t');
        }).join('\n');
        await writeClipboard(tsv);
        if (k === 'x' && !readOnly) { setRange(() => ''); setSaveMsg('Cut'); }
        else setSaveMsg('Copied!');
        setTimeout(() => setSaveMsg(null), 1500);
        return;
      }

      if (k === 'v') {
        e.preventDefault();
        if (readOnly) return;
        let text = '';
        try { text = await navigator.clipboard.readText(); } catch { return; }
        if (!text) return;
        const cells = parseTSV(text.replace(/\n$/, ''));
        if (!cells.length) return;
        // Pasting with the cursor on the computed taxonomy column starts at the first field.
        const pasteAnchorFi = Math.max(0, Math.min(fi, fields.length - 1));
        const { p1, p2, f2 } = getRange();

        // One value into a multi-cell selection fills the selection, as in Excel.
        if (cells.length === 1 && cells[0].length === 1 && (p2 > p1 || f2 > pasteAnchorFi)) {
          const only = cells[0][0].trim();
          setRange(() => only);
          return;
        }

        const startPos  = Math.max(0, posOf(ri));
        const flashKeys = [];
        let droppedRows = 0, overflowCols = false;
        setRows(prev => {
          pushUndo(prev);
          const next = prev.map(r => ({ ...r, values: { ...r.values } }));
          const view = [...viewIndices];
          cells.forEach((tsvRow, rOff) => {
            const pos = startPos + rOff;
            let tRi;
            if (pos < view.length) tRi = view[pos];
            else if (canGrow) { tRi = next.length; next.push(mkRow()); view.push(tRi); }
            else { droppedRows++; return; }   // filtered/sorted: no room, and no invisible writes
            tsvRow.forEach((val, fOff) => {
              const tFi = pasteAnchorFi + fOff;
              if (tFi < fields.length) { next[tRi].values[fields[tFi].id] = coerceForField(fields[tFi], val); flashKeys.push(`${tRi},${tFi}`); }
              else overflowCols = true;
            });
          });
          return next;
        });
        flash(flashKeys);
        const endPos = Math.min(startPos + cells.length - 1, viewIndices.length - 1 + (canGrow ? cells.length : 0));
        setSel({ ri, fi: pasteAnchorFi });
        setSelEnd({ ri: riAt(endPos), fi: Math.min(pasteAnchorFi + (cells[0]?.length || 1) - 1, LAST_FI) });
        if (droppedRows || overflowCols) {
          const bits = [];
          if (droppedRows)  bits.push(`${droppedRows} row${droppedRows === 1 ? '' : 's'} had nowhere to go — clear the filter or sort first`);
          if (overflowCols) bits.push('some columns fell past the last field');
          setSaveMsg(`Pasted, but ${bits.join('; ')}.`);
          setTimeout(() => setSaveMsg(null), 6000);
        }
        scheduleAutosave(true);
        return;
      }
    }
    if ((e.key === 'Delete' || e.key === 'Backspace') && !editCell) {
      e.preventDefault();
      if (readOnly) return;
      setRange(() => '');
      return;
    }
    if (!editCell && !e.ctrlKey && !e.metaKey && !e.altKey && e.key.length === 1) {
      if (readOnly) return;
      // The taxonomy column is computed, not stored — there is no field behind it, so a
      // keystroke there has nothing to write to.
      if (fi < 0) return;
      // The cursor can only be on a visible row now, but guard anyway: a keystroke must never
      // land on a row the user cannot see.
      if (posOf(ri) < 0) return;
      editingSnapshotRef.current = rows;
      // A dictionary cell searches rather than overwrites: the keystroke opens the editor as
      // its first query letter, and the cell keeps its value until something is chosen. Typing
      // a stray character can no longer blank a validated cell.
      if (fields[fi]?.field_type === 'dropdown') {
        pendingQueryRef.current = e.key;
        setEditCell({ ri, fi });
        return;
      }
      pendingQueryRef.current = '';
      setEditCell({ ri, fi }); setCell(ri, fi, '');
      setTimeout(() => { const el = inputRefs.current[`${ri},${fi}`]; if (el) { el.focus(); el.value = e.key; setCell(ri, fi, e.key); } }, 0);
    }
  }, [sel, selEnd, editCell, rows, fields, getRange, rangeRows, navigate, cancelEdit, pushUndo,
      scheduleAutosave, posOf, riAt, viewIndices, canGrow, setRange, fillDown, fillRight, readOnly,
      taxonomy, dictValues, TAX_COL, LAST_FI, selRows, deleteSelRows, insertSelRows, clearSelRows,
      setRows, taxStrings, V_H]);

  // ── Fill handle ───────────────────────────────────────────────────────────
  // Dragging the corner repeats the selected block downwards, cycling it the way Excel does
  // when the source is several rows tall.
  /**
   * Write a fill block. Along the row axis each column continues its own series down (or up)
   * the target; along the column axis each row continues its own series sideways.
   */
  const runFill = useCallback((src, t) => {
    if (!src || !t || t.p2 < t.p1 || t.f2 < t.f1) return;
    const lastFi = Math.min(src.f2, fields.length - 1);
    const srcRows = viewIndices.slice(src.p1, src.p2 + 1);
    const tgtRows = viewIndices.slice(t.p1, t.p2 + 1);
    if (!srcRows.length || !tgtRows.length) return;
    const back = t.dir < 0;
    const flashKeys = [];
    setRows(prev => {
      pushUndo(prev);
      const next = prev.map(r => ({ ...r, values: { ...r.values } }));
      if (t.axis === 'row') {
        for (let fi = Math.max(0, t.f1); fi <= t.f2; fi++) {
          const nextVal = seriesFor(srcRows.map(sr => next[sr].values[fields[fi].id] ?? ''), back);
          // Filling upwards writes from the source outwards, so `n` counts distance from it.
          tgtRows.forEach((tri, off) => {
            const n = back ? tgtRows.length - 1 - off : off;
            next[tri].values[fields[fi].id] = nextVal(n);
            flashKeys.push(`${tri},${fi}`);
          });
        }
      } else {
        const srcCols = [];
        for (let fi = Math.max(0, src.f1); fi <= lastFi; fi++) srcCols.push(fi);
        tgtRows.forEach(tri => {
          const nextVal = seriesFor(srcCols.map(fi => next[tri].values[fields[fi].id] ?? ''), back);
          for (let fi = Math.max(0, t.f1); fi <= t.f2; fi++) {
            const n = back ? t.f2 - fi : fi - t.f1;
            next[tri].values[fields[fi].id] = nextVal(n);
            flashKeys.push(`${tri},${fi}`);
          }
        });
      }
      return next;
    });
    flash(flashKeys);
    setSel({ ri: viewIndices[Math.min(src.p1, t.p1)], fi: Math.min(src.f1, t.f1) });
    setSelEnd({ ri: viewIndices[Math.max(src.p2, t.p2)], fi: Math.max(lastFi, t.f2) });
    scheduleAutosave(true);
  }, [viewIndices, fields, setRows, pushUndo, scheduleAutosave]);

  const applyFill = useCallback(() => {
    const src = fillFrom.current;
    const t = fillTarget();
    fillFrom.current = null;
    setFillTo(null);
    runFill(src, t);
  }, [fillTarget, runFill]);

  /**
   * Double-clicking the handle fills to the bottom of the run of data beside the selection —
   * Excel's most-used gesture, and previously the one thing the handle could not do. The edge
   * is taken from the neighbouring column so a blank target column still gets filled.
   */
  const fillToBottom = useCallback(() => {
    if (readOnly) return;
    const { p1, p2, f1, f2 } = getRange();
    if (p2 < p1) return;
    const lastFi = Math.min(f2, fields.length - 1);
    if (lastFi < 0) return;   // taxonomy column only — nothing to fill
    const from = Math.max(0, f1);
    const probe = from > 0 ? from - 1 : lastFi + 1 <= fields.length - 1 ? lastFi + 1 : -1;
    let end = p2;
    if (probe >= 0) {
      while (end + 1 < viewIndices.length
             && String(rows[viewIndices[end + 1]].values[fields[probe].id] ?? '').trim() !== '') end++;
    }
    // Nothing to key off: fall back to the last row of the sheet, which is what the user
    // means by "to the bottom" when there is no neighbouring column to stop at.
    if (end === p2) end = viewIndices.length - 1;
    if (end <= p2) return;
    runFill({ p1, p2, f1: from, f2 }, { p1: p2 + 1, p2: end, f1: from, f2: lastFi, axis: 'row', dir: 1 });
  }, [readOnly, getRange, fields, viewIndices, rows, runFill]);

  useEffect(() => {
    if (!fillFrom.current) return;
    const up = () => applyFill();
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, [applyFill, fillTo]);

  // ── Find / replace ────────────────────────────────────────────────────────
  const matchesQuery = useCallback((val, q, matchCase) =>
    matchCase ? String(val).includes(q) : String(val).toLowerCase().includes(q.toLowerCase()),
  []);

  /** Walk the visible grid in reading order from just after the cursor, wrapping once. */
  const findNext = useCallback(() => {
    const q = find?.q || '';
    if (!q || !viewIndices.length) return;
    const startPos = sel ? Math.max(0, posOf(sel.ri)) : 0;
    const startFi  = sel ? sel.fi : -1;
    const total    = viewIndices.length * fields.length;
    let cursor = startPos * fields.length + startFi + 1;
    for (let n = 0; n < total; n++) {
      const at = (cursor + n) % total;
      const p  = Math.floor(at / fields.length);
      const fi = at % fields.length;
      const ri = viewIndices[p];
      if (matchesQuery(rows[ri].values[fields[fi].id] ?? '', q, find.matchCase)) {
        setSel({ ri, fi }); setSelEnd(null); setEditCell(null);
        if (scrollRef.current) scrollRef.current.scrollTop = Math.max(0, p * ROW_H - V_H / 2);
        setFind(f => ({ ...f, msg: null }));
        return;
      }
    }
    setFind(f => ({ ...f, msg: 'No match' }));
  }, [find, viewIndices, fields, rows, sel, posOf, matchesQuery]);

  /** Replace in the selected cell if it matches, then advance. */
  const replaceOne = useCallback(() => {
    if (readOnly || !find?.q || !sel) return;
    const { ri, fi } = sel;
    if (fi < 0) { findNext(); return; }
    const cur = String(rows[ri].values[fields[fi].id] ?? '');
    if (matchesQuery(cur, find.q, find.matchCase)) {
      const re = new RegExp(find.q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), find.matchCase ? 'g' : 'gi');
      setRows(prev => {
        pushUndo(prev);
        const next = prev.map(r => ({ ...r, values: { ...r.values } }));
        next[ri].values[fields[fi].id] = cur.replace(re, find.replace);
        return next;
      });
      scheduleAutosave(true);
    }
    findNext();
  }, [readOnly, find, sel, rows, fields, matchesQuery, findNext, setRows, pushUndo, scheduleAutosave]);

  /** Replace across every visible cell — hidden rows are left alone, as in Excel. */
  const replaceAll = useCallback(() => {
    if (readOnly || !find?.q) return;
    const re = new RegExp(find.q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), find.matchCase ? 'g' : 'gi');
    let count = 0;
    setRows(prev => {
      pushUndo(prev);
      const next = prev.map(r => ({ ...r, values: { ...r.values } }));
      viewIndices.forEach(ri => {
        fields.forEach(f => {
          const cur = String(next[ri].values[f.id] ?? '');
          if (cur && matchesQuery(cur, find.q, find.matchCase)) {
            next[ri].values[f.id] = cur.replace(re, find.replace);
            count++;
          }
        });
      });
      return next;
    });
    setFind(f => ({ ...f, msg: count ? `Replaced ${count}` : 'No match' }));
    if (count) scheduleAutosave(true);
  }, [readOnly, find, viewIndices, fields, matchesQuery, setRows, pushUndo, scheduleAutosave]);

  /**
   * Clicking a sort control cycles that column asc → desc → off, and makes it the primary
   * sort. Shift-clicking appends instead, which is how a second and third key are added
   * without losing the first.
   */
  const toggleSort = (colKey, additive = false) => setSortSpec(prev => {
    const existing = prev.find(s => s.colKey === colKey);
    const next = existing?.dir === 'asc' ? { colKey, dir: 'desc' } : existing ? null : { colKey, dir: 'asc' };
    if (!additive) return next ? [next] : [];
    const rest = prev.filter(s => s.colKey !== colKey);
    return next ? [...rest, next] : rest;
  });

  const sortRank = (colKey) => sortSpec.findIndex(s => s.colKey === colKey);
  const colName  = (colKey) => colKey === '__tax__'
    ? outputLabel
    : (fields.find(f => String(f.id) === colKey)?.name || 'column');

  const insertRowAbove = (ri) => {
    setRows(prev => { pushUndo(prev); return [...prev.slice(0, ri), mkRow(), ...prev.slice(ri)]; });
    scheduleAutosave(false); // short debounce — lets an immediately-following paste batch into one save
  };
  const insertRowBelow = (ri) => {
    setRows(prev => { pushUndo(prev); return [...prev.slice(0, ri + 1), mkRow(), ...prev.slice(ri + 1)]; });
    scheduleAutosave(false);
  };

  // Manual "Save now" button — forces an immediate save via the same autosave pipeline.
  // Validation errors no longer block saving (data must never be lost to a refresh/session expiry);
  // the error badge below still shows so the user knows what to fix.
  const saveCampaigns = () => {
    if (adapter) { scheduleAutosave(true); return; }
    if (!campaignName?.trim()) { setSaveMsg('Enter a campaign name first.'); setTimeout(() => setSaveMsg(null), 3000); return; }
    if (!rows.some(rowHasContent)) { setSaveMsg('Fill in some cells first.'); setTimeout(() => setSaveMsg(null), 3000); return; }
    scheduleAutosave(true);
  };

  const copyAll = () => {
    // Screen order: taxonomy string first, then the fields that build it.
    const header = [outputLabel, ...fields.map(f => f.name)].join('\t');
    const body = rows.map((r, ri) =>
      [taxStrings[ri] || '', ...fields.map(f => r.values[f.id] || '')]
        .map(c => /[\t\n"]/.test(c) ? `"${String(c).replace(/"/g, '""')}"` : c).join('\t'));
    writeClipboard([header, ...body].join('\n'));
    setSaveMsg('Copied!'); setTimeout(() => setSaveMsg(null), 1500);
  };

  // Chips are identical wherever they appear; brass here is a status wash, one of its
  // sanctioned uses, not a fill.
  const chip = {
    fontSize: 11, color: C.brass, background: C.brassWash,
    border: `1px solid ${C.brass}44`, borderRadius: T.rSm, padding: '2px 8px',
  };
  const chipBtn = {
    background: 'none', border: 'none', color: C.brass, cursor: 'pointer',
    fontSize: 12, padding: '0 0 0 6px', fontFamily: 'inherit',
  };

  if (!taxonomy) return null;

  // Grid geometry is load-bearing: ROW_H (36) drives the virtualization maths. The cell chrome
  // controls colour and weight only — padding and font-size must not change without updating
  // ROW_H to match (see THcell / TDcell at module scope).
  const completeRows = adapter ? rows.filter(adapter.isComplete).length : taxStrings.filter(Boolean).length;
  const corner = readOnly ? null : rangeCorner();

  // The selection and the fill preview reach a row as four numbers each, so an unchanged row
  // can skip re-rendering. See GridRow for why the interface is shaped this way.
  const selRect  = getRange();
  const fillRect = fillTarget() || { p1: 0, p2: -1, f1: 0, f2: -1 };

  // Everything a row calls, rebuilt each render but reached through a ref whose identity never
  // changes — so the memo holds and no row can call a stale closure.
  const ctxRef = useRef({});
  ctxRef.current = {
    inputRefs,
    setCell,
    setHoverRow,
    setCtxMenu,
    pendingQuery: () => pendingQueryRef.current,

    onRowNumberClick: (e, ri) => {
      if (readOnly) return;
      if (e.ctrlKey || e.metaKey) {
        setSelRows(prev => { const n = new Set(prev); n.has(ri) ? n.delete(ri) : n.add(ri); return n; });
        lastRowAnchor.current = ri;
        setSel(null); setSelEnd(null); setEditCell(null);
      } else if (e.shiftKey && lastRowAnchor.current !== null) {
        // Span the range on screen, not in storage — otherwise a shift-select across a filter
        // quietly picks up every hidden row in between, and "Delete rows" then destroys records
        // that were never shown.
        const a = posOf(lastRowAnchor.current), b = posOf(ri);
        if (a >= 0 && b >= 0) {
          const lo = Math.min(a, b), hi = Math.max(a, b);
          setSelRows(new Set(viewIndices.slice(lo, hi + 1)));
        }
        setSel(null); setSelEnd(null); setEditCell(null);
      } else {
        setSelRows(new Set([ri]));
        lastRowAnchor.current = ri;
        setSel(null); setSelEnd(null); setEditCell(null);
      }
      tableRef.current?.focus();
    },

    onCellMouseDown: (e, ri, fi) => {
      // A click that lands inside an open editor belongs to the editor: preventing the default
      // here is what stopped the date input's calendar button from doing anything, and would
      // equally break selecting text in a cell being edited.
      if (e.target.closest('input, textarea, [data-editor]')) return;
      // Selection is available on a record too — it is the only way to get data out of a
      // campaign that cannot be edited. Editing stays blocked.
      setSelRows(new Set());
      dragMovedRef.current = false;
      if (e.shiftKey && sel) { e.preventDefault(); setSelEnd({ ri, fi }); setEditCell(null); }
      else {
        setSel({ ri, fi }); setSelEnd(null); setEditCell(null);
        isDragging.current = true;
        e.preventDefault(); tableRef.current?.focus();
      }
    },

    onCellMouseEnter: (ri, fi, pos) => {
      if (fillFrom.current) { setFillTo({ pos, fi }); return; }
      if (isDragging.current) { dragMovedRef.current = true; setSelEnd({ ri, fi }); setEditCell(null); }
    },

    onTaxDoubleClick: (taxStr) => {
      if (!taxStr) return;
      writeClipboard(taxStr);
      setSaveMsg('Copied!'); setTimeout(() => setSaveMsg(null), 1500);
    },

    openEditor: (ri, fi, { ifNotDragged = false, focus = false, picker = false } = {}) => {
      if (readOnly) return;
      if (ifNotDragged && dragMovedRef.current) return;
      pendingQueryRef.current = '';
      editingSnapshotRef.current = rowsRef.current;
      setSel({ ri, fi }); setSelEnd(null); setEditCell({ ri, fi });
      if (focus || picker) {
        setTimeout(() => {
          const el = inputRefs.current[`${ri},${fi}`];
          if (!el) return;
          el.focus();
          // A date cell opens its calendar on the same single click that selects it, rather
          // than making the user find the small icon inside an editor they first had to
          // double-click into. showPicker is still inside the click's user activation here;
          // where a browser does not have it, focus alone leaves a usable field.
          if (picker) { try { el.showPicker?.(); } catch { /* not allowed without a gesture */ } }
        }, 0);
      }
    },

    commitCell: (ri, fi, v) => {
      setCell(ri, fi, v);
      setEditCell(null);
      // The snapshot was taken when the editor opened, so this records one undo step for the
      // change and saves it immediately.
      setTimeout(() => { commitEdit(); tableRef.current?.focus(); }, 0);
    },

    // Header handlers live here for the same reason the row's do: it keeps the memoized
    // <thead> from depending on state that changes on every keystroke.
    toggleSort:    (colKey, additive) => toggleSort(colKey, additive),
    openFilterFor: (colKey, e) => openFilterFor(colKey, e),
    selectColumn:  (fi) => selectColumn(fi),
    clearFilters:  () => setActiveFilters({}),
    setColWidths,

    closeEditor: () => { setEditCell(null); tableRef.current?.focus(); },
    blurEditor:  () => { commitEdit(); setEditCell(null); },
    navigateLater: (ri, fi, dir) => setTimeout(() => navigate(ri, fi, dir), 0),

    onEditorKeyDown: (e, ri, fi, { allowAltEnter = false } = {}) => {
      if (e.key === 'Tab') { e.preventDefault(); e.stopPropagation(); navigate(ri, fi, e.shiftKey ? 'left' : 'right'); }
      else if (allowAltEnter && e.key === 'Enter' && e.altKey) {
        e.stopPropagation();   // let the newline through; the browser inserts it for us
      } else if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); navigate(ri, fi, 'down'); }
      else if (e.key === 'Escape') { e.stopPropagation(); cancelEdit(); setEditCell(null); tableRef.current?.focus(); }
    },

    startFill: () => {
      isDragging.current = false;
      const { p1, p2, f1, f2 } = getRange();
      fillFrom.current = { p1, p2, f1, f2 };
      setFillTo({ pos: p2, fi: Math.min(f2, fields.length - 1) });
    },
    fillToBottom: () => fillToBottom(),
  };

  /**
   * The values offered in a column's checklist, scoped to the rows the *other* columns'
   * filters leave visible — Excel's behaviour, and the only one that makes sense: a list
   * offering values that cannot appear alongside the filters already set is a list of dead
   * ends. Computed on open rather than for every column on every keystroke.
   */
  const valuesForFilter = (colKey) => {
    const others = Object.entries(activeFilters).filter(([k, s]) => k !== colKey && s != null);
    const seen = new Set();
    for (let ri = 0; ri < rows.length; ri++) {
      if (others.every(([k, spec]) => matchesFilter(valueAt(ri, k), spec))) seen.add(valueAt(ri, colKey));
    }
    // A value already being filtered on must stay listed even if the other filters now
    // exclude it, or reopening the menu silently drops part of the active selection.
    const cur = activeFilters[colKey];
    if (cur?.kind === 'values') cur.set.forEach(v => seen.add(v));
    return [...seen].sort((a, b) => compareValues(a, b, 1));
  };

  const openFilterFor = (colKey, e) => {
    e.stopPropagation();
    if (openFilter === colKey) { setOpenFilter(null); return; }
    setFilterRect(e.currentTarget.getBoundingClientRect());
    setOpenFilter(colKey);
  };

  const selectColumn = (fi) => {
    if (!viewIndices.length) return;
    setSel({ ri: viewIndices[0], fi });
    setSelEnd({ ri: viewIndices[viewIndices.length - 1], fi });
    setEditCell(null);
  };

  return (
    <div onKeyDownCapture={e => {
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      const k = e.key.toLowerCase();
      // Find works on a record too — searching a saved campaign is reading, not editing.
      // Caught here rather than on the table so it works while a cell editor holds focus.
      if (k === 'f') { e.preventDefault(); setFind(f => f || { q: '', replace: '', matchCase: false, msg: null }); return; }
      if (readOnly) return;
      if (k === 'z' && !e.shiftKey) { e.preventDefault(); doUndo(); }
      else if (k === 'y' || (k === 'z' && e.shiftKey)) { e.preventDefault(); doRedo(); }
      else if (k === 's') { e.preventDefault(); saveCampaigns(); } // force an immediate save, like Excel's Ctrl+S
    }}>
      {/* Status chips read the same whether or not the campaign can be edited — a record still
          gets filtered and sorted, and still has to say how many rows it is hiding. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <span style={{ ...lbl(), marginRight: 4 }}>
          {readOnly ? `${rows.length} row${rows.length !== 1 ? 's' : ''}` : `${completeRows} of ${rows.length} rows complete`}
        </span>
        {hasActiveFilters && (
          <span style={chip}>
            {filteredIndices.length}/{rows.length} rows shown
            {/* The set is frozen at apply time so a row cannot vanish mid-keystroke; this is
                how you ask for it to be judged again, as Excel's Data ▸ Reapply does. */}
            {staleFilter && (
              <button onClick={reapplyFilters} title="Re-evaluate the filter against the current values" style={chipBtn}>reapply</button>
            )}
            <button onClick={() => setActiveFilters({})} style={chipBtn}>clear</button>
          </span>
        )}
        {sortSpec.length > 0 && (
          <span style={chip}>
            sorted by {sortSpec.map(s => `${colName(s.colKey)}${s.dir === 'asc' ? ' ▲' : ' ▼'}`).join(', then ')}
            <button onClick={() => setSortSpec([])} style={chipBtn}>clear</button>
          </span>
        )}
        <div style={{ flex: 1 }} />
        {readOnly && (
          <button onClick={copyAll} style={btn()}>Copy all</button>
        )}
      </div>

      {!readOnly && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button onClick={() => addRows(1)} disabled={!canGrow}
            title={canGrow ? undefined : 'Clear the filter and sort to add rows'}
            style={{ ...btn(), opacity: canGrow ? 1 : 0.5 }}>+ Row</button>
          <button onClick={() => { if (canGrow) addRows(addCount, rows[rows.length - 1]?.values || {}); }} disabled={!canGrow}
            title={canGrow ? undefined : 'Clear the filter and sort to add rows'}
            style={{ ...btn(), display: 'flex', alignItems: 'center', gap: 4, opacity: canGrow ? 1 : 0.5 }}>
            +{' '}
            <input
              type="number" value={addCount} min={1} max={100}
              onClick={e => e.stopPropagation()}
              onChange={e => setAddCount(Math.max(1, parseInt(e.target.value) || 1))}
              style={{ width: 32, background: 'transparent', border: 'none', color: C.text, fontSize: 12, fontFamily: 'inherit', outline: 'none', textAlign: 'center' }}
            />{' '}rows
          </button>
          <div style={{ width: 1, height: 18, background: C.border }} />
          <button onClick={() => setShowImport(true)} style={{ ...btn(), display: 'flex', alignItems: 'center', gap: 5 }}>
            ⬆ Import
          </button>
          {allowBatchGenerate && <button onClick={() => setShowBatch(true)} style={{ ...btn(), display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ color: C.warn }}>⚡</span> Batch Generate
          </button>}
          <button onClick={copyAll} style={btn()}>Copy all</button>
          <button onClick={saveCampaigns} disabled={saving || nameBlocked}
            title={nameBlocked ? 'Campaign name already exists — choose a unique name' : undefined}
            style={{ ...btn('primary'), opacity: (saving || nameBlocked) ? 0.5 : 1 }}>
            {saving ? 'Saving…' : '💾 Save now'}
          </button>
          {/* The error badge and the save status are separate slots on purpose. They shared one
              if/return chain, and the badge won it — so a sheet with any validation error showed
              no save state at all, including "enter a campaign name", which is the one message
              that explains why nothing is being written. */}
          {(() => {
            const errCount = Object.values(validationErrors).reduce((s, r) => s + Object.keys(r).length, 0);
            if (!errCount) return null;
            return (
              <button onClick={() => setShowErrDetails(v => !v)} style={{
                fontSize: 11, fontWeight: 700, color: C.danger,
                background: `${C.danger}18`, border: `1px solid ${C.danger}44`,
                borderRadius: 12, padding: '3px 8px', cursor: 'pointer', fontFamily: 'inherit',
              }}>
                ⚠ {errCount} error{errCount !== 1 ? 's' : ''} {showErrDetails ? '▴' : '▾'}
              </button>
            );
          })()}
          {(() => {
            if (adapter) return <span role="status" className="o-note">{saveMsg || adapter.status}</span>;
            if (saveMsg) return (
              // Green only for the two messages that report something went right. Testing for
              // failure words instead made "Enter a campaign name first." read as a success.
              <span style={{ fontSize: 12, fontWeight: 600, color: /copied|saved/i.test(saveMsg) ? C.success : C.danger }}>
                {saveMsg}
              </span>
            );
            if (saveStatus.state === 'saving') return (
              <span style={{ fontSize: 12, fontWeight: 600, color: C.muted }}>Saving…</span>
            );
            if (saveStatus.state === 'retrying') return (
              <span style={{ fontSize: 12, fontWeight: 600, color: C.warn }}>Save failed — retrying…</span>
            );
            if (saveStatus.state === 'error') return (
              <span style={{ fontSize: 12, fontWeight: 600, color: C.danger }} title={saveStatus.msg}>Save failed{saveStatus.msg ? `: ${saveStatus.msg}` : ''}</span>
            );
            if (saveStatus.state === 'saved') return (
              <span style={{ fontSize: 12, fontWeight: 600, color: C.success }}>✓ All changes saved</span>
            );
            return null;
          })()}
        </div>
      )}

      {!readOnly && showErrDetails && Object.keys(validationErrors).length > 0 && (
        <div style={{ background: `${C.danger}0C`, border: `1px solid ${C.danger}33`, borderRadius: 8,
          padding: '10px 14px', marginBottom: 10, fontSize: 12 }}>
          <div style={{ fontWeight: 700, color: C.danger, marginBottom: 8, fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            Error Details
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {Object.entries(validationErrors).map(([ri, fieldErrs]) => (
              <div key={ri} style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700, color: C.danger, flexShrink: 0, minWidth: 56 }}>
                  Row {Number(ri) + 1}
                </span>
                <span style={{ color: C.text }}>
                  {Object.entries(fieldErrs).map(([fi, msg]) => (
                    <span key={fi}>
                      <span style={{ fontWeight: 600, color: C.text }}>{Number(fi) === TAX_COL ? outputLabel : fields[fi]?.name || `Field ${fi}`}</span>
                      <span style={{ color: C.muted }}>{': '}{msg}</span>
                    </span>
                  )).reduce((acc, el, i) => i === 0 ? [el] : [...acc, <span key={`sep-${i}`} style={{ color: C.border }}> · </span>, el], [])}
                </span>
              </div>
            ))}
          </div>
          {errorDetails}
        </div>
      )}

      {!readOnly && (
        <div style={{ fontSize: 10, color: C.muted, marginBottom: 8, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <span><b style={{ color: C.dim }}>Click</b> to select · <b style={{ color: C.dim }}>Double-click</b> or <b style={{ color: C.dim }}>F2</b> to edit · <b style={{ color: C.dim }}>Type</b> to replace · <b style={{ color: C.dim }}>Shift+Click</b> to extend · <b style={{ color: C.dim }}>drag the corner</b> to fill, <b style={{ color: C.dim }}>double-click it</b> to fill to the bottom</span>
          <span><b style={{ color: C.dim }}>Ctrl+C/X/V</b> copy, cut, paste · <b style={{ color: C.dim }}>Ctrl+D/R</b> fill down, right · <b style={{ color: C.dim }}>Ctrl+A</b> select all · <b style={{ color: C.dim }}>Ctrl+F</b> find · <b style={{ color: C.dim }}>Del</b> clear · <b style={{ color: C.dim }}>Ctrl+Z/Y</b> undo, redo · <b style={{ color: C.dim }}>Ctrl+S</b> save</span>
          <span><b style={{ color: C.dim }}>Ctrl/Shift+Space</b> select column, row · <b style={{ color: C.dim }}>Ctrl +/−</b> insert, delete rows · <b style={{ color: C.dim }}>Ctrl+Shift+L</b> filters · <b style={{ color: C.dim }}>Alt+Enter</b> line break</span>
          <span><b style={{ color: C.dim }}>Home/End</b> row ends · <b style={{ color: C.dim }}>Ctrl+Arrows</b> jump to edge · <b style={{ color: C.dim }}>PgUp/PgDn</b> page · <b style={{ color: C.dim }}>↕</b> sort (Shift-click to add a key) · drag a header edge to resize</span>
        </div>
      )}

      {find && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap',
          background: C.s2, border: `1px solid ${C.border}`, borderRadius: 12, padding: '8px 10px',
        }}>
          <input
            autoFocus
            value={find.q}
            placeholder="Find in visible rows…"
            onChange={e => setFind(f => ({ ...f, q: e.target.value, msg: null }))}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); findNext(); } if (e.key === 'Escape') { e.stopPropagation(); setFind(null); } }}
            style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '4px 8px', fontSize: 12, fontFamily: 'inherit', color: C.text, outline: 'none', width: 180 }}
          />
          {!readOnly && (
            <input
              value={find.replace}
              placeholder="Replace with…"
              onChange={e => setFind(f => ({ ...f, replace: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Escape') { e.stopPropagation(); setFind(null); } }}
              style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '4px 8px', fontSize: 12, fontFamily: 'inherit', color: C.text, outline: 'none', width: 180 }}
            />
          )}
          <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: C.dim, cursor: 'pointer' }}>
            <input type="checkbox" checked={find.matchCase} style={{ accentColor: C.ink }}
              onChange={e => setFind(f => ({ ...f, matchCase: e.target.checked }))} />
            Match case
          </label>
          <button className="o-btn o-btn-sm" onClick={findNext}>Find next</button>
          {!readOnly && <button className="o-btn o-btn-sm" onClick={replaceOne}>Replace</button>}
          {!readOnly && <button className="o-btn o-btn-sm" onClick={replaceAll}>Replace all</button>}
          {find.msg && <span style={{ fontSize: 11, color: C.muted }}>{find.msg}</span>}
          <div style={{ flex: 1 }} />
          <button onClick={() => setFind(null)} title="Close (Esc)"
            style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 16, lineHeight: 1, fontFamily: 'inherit' }}>×</button>
        </div>
      )}

      <style>{`
        .sg-scroll::-webkit-scrollbar { width: 8px; height: 8px; }
        .sg-scroll::-webkit-scrollbar-track { background: ${C.bg}; border-radius: 4px; }
        .sg-scroll::-webkit-scrollbar-thumb { background: ${C.border}; border-radius: 4px; }
        .sg-scroll::-webkit-scrollbar-thumb:hover { background: ${C.muted}; }
        .sg-scroll::-webkit-scrollbar-corner { background: ${C.bg}; }
        .sg-scroll select { background: transparent; }
        .sg-scroll select:focus { background: ${C.s2}; }
        .sg-scroll select option { background: ${C.s2}; color: ${C.text}; }
      `}</style>
      <div
        ref={scrollRef}
        className="sg-scroll"
        style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: V_H, borderRadius: 12, border: `1px solid ${C.border}`, paddingBottom: 8 }}
        onScroll={onScroll}
        onMouseDown={e => { if (!tableRef.current?.contains(e.target)) { setSel(null); setSelEnd(null); setEditCell(null); } }}
      >
        <table ref={tableRef} style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed' }}
          onKeyDown={onKeyDown} tabIndex={-1}
        >
          {/* The computed taxonomy string comes first, then the fields that build it — the same
              order Ctrl+C and "Copy all" write, and the same order the cursor moves in. Render
              order and index order must not diverge: rendering it on one side while indexing it
              on the other is what once sent Arrow-Right jumping across the sheet, so it is
              indexed at TAX_COL = -1, left of every field. It stays pinned so a wide sheet
              never scrolls the string away. */}
          <colgroup>
            <col style={{ width: 36 }} />
            <col style={{ width: colWidths['__tax__'] || 520 }} />
            {fields.map(f => (
              <col key={f.id} style={{ width: colWidths[String(f.id)] || Math.max(120, f.name.length * 9 + 40) }} />
            ))}
          </colgroup>
          <GridHead
            outputLabel={outputLabel}
            fields={fields}
            sortSpec={sortSpec}
            activeFilters={activeFilters}
            filtersOn={filtersOn}
            colWidths={colWidths}
            hasActiveFilters={hasActiveFilters}
            ctxRef={ctxRef}
          />
          <tbody>
            {padTop > 0 && <tr style={{ height: padTop }}><td colSpan={colSpan} style={{ padding: 0, border: 'none' }} /></tr>}
            {viewIndices.slice(visStart, visEnd).map((ri, offset) => (
              <GridRow
                key={rows[ri].id}
                row={rows[ri]}
                ri={ri}
                pos={visStart + offset}
                taxStr={taxStrings[ri]}
                fields={fields}
                dictValues={dictValues}
                readOnly={readOnly}
                ctxRef={ctxRef}
                p1={selRect.p1} p2={selRect.p2} f1={selRect.f1} f2={selRect.f2}
                anchorRi={sel ? sel.ri : -1}
                anchorFi={sel ? sel.fi : -2}
                fp1={fillRect.p1} fp2={fillRect.p2} ff1={fillRect.f1} ff2={fillRect.f2}
                editFi={editCell && editCell.ri === ri ? editCell.fi : -2}
                rowSelected={selRows.has(ri)}
                rowHovered={hoverRow === ri}
                rowErrs={validationErrors[ri]}
                flashSet={flashSet}
                cornerFi={corner && corner.ri === ri ? corner.fi : -2}
                taxWidth={colWidths['__tax__'] || 520}
                reviewFields={adapter ? rows[ri].review || {} : undefined}
                outputHint={adapter?.outputHint(rows[ri])}
              />
            ))}
            {padBottom > 0 && <tr style={{ height: padBottom }}><td colSpan={colSpan} style={{ padding: 0, border: 'none' }} /></tr>}
          </tbody>
        </table>
      </div>

      {/* Filter dropdown */}
      {openFilter && filterRect && (
        <FilterDropdown
          allValues={valuesForFilter(openFilter)}
          active={activeFilters[openFilter] ?? null}
          sortDir={sortSpec.find(s => s.colKey === openFilter)?.dir ?? null}
          onSort={dir => {
            setSortSpec(prev => [{ colKey: openFilter, dir }, ...prev.filter(s => s.colKey !== openFilter)].slice(0, 1));
            setOpenFilter(null);
          }}
          onApply={spec => {
            setActiveFilters(prev => ({ ...prev, [openFilter]: spec }));
            setOpenFilter(null);
          }}
          onClose={() => setOpenFilter(null)}
          anchorRect={filterRect}
        />
      )}

      {ctxMenu && (
        <div className="sg-ctx-menu" ref={ctxMenuRef} style={{
          position: 'fixed',
          top:  ctxPos ? ctxPos.top  : ctxMenu.y,
          left: ctxPos ? ctxPos.left : ctxMenu.x,
          // Hidden for the single measuring frame so it never flashes at the unclamped spot.
          visibility: ctxPos ? 'visible' : 'hidden',
          zIndex: 9999,
          maxHeight: 'calc(100vh - 16px)', overflowY: 'auto',
          background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12,
          boxShadow: '0 4px 20px rgba(0,0,0,0.5)', minWidth: 190,
          fontFamily: 'inherit',
        }}>
          {[
            { label: 'Copy row', icon: '⎘', action: async () => {
              const row = rows[ctxMenu.ri];
              if (!row) return;
              const cells = [...fields.map(f => row.values[f.id] || ''), taxStrings[ctxMenu.ri] || ''];
              await writeClipboard(cells.map(c => /[\t\n"]/.test(c) ? `"${String(c).replace(/"/g, '""')}"` : c).join('\t'));
              setSaveMsg('Row copied!'); setTimeout(() => setSaveMsg(null), 1500); setCtxMenu(null);
            }},
            { label: 'Paste into row', icon: '⎘', action: async () => {
              let text = '';
              try { text = await navigator.clipboard.readText(); } catch { setCtxMenu(null); return; }
              if (!text) { setCtxMenu(null); return; }
              const tRi = ctxMenu.ri;
              // Same parser and same view-space walk as Ctrl+V: splitting on \t/\n scatters any
              // Excel cell holding a tab or a line break, and stepping through storage indices
              // writes into rows the filter is hiding.
              const tsvRows = parseTSV(text.replace(/\n$/, ''));
              if (!tsvRows.length) { setCtxMenu(null); return; }
              const startPos = Math.max(0, posOf(tRi));
              const flashKeys = [];
              let dropped = 0, lastPos = startPos;
              setRows(prev => {
                pushUndo(prev);
                const next = prev.map(r => ({ ...r, values: { ...r.values } }));
                const view = [...viewIndices];
                tsvRows.forEach((tsvRow, rOff) => {
                  const pos = startPos + rOff;
                  let targetRi;
                  if (pos < view.length) targetRi = view[pos];
                  else if (canGrow) { targetRi = next.length; next.push(mkRow()); view.push(targetRi); }
                  else { dropped++; return; }
                  lastPos = pos;
                  tsvRow.forEach((val, fOff) => {
                    if (fOff < fields.length) { next[targetRi].values[fields[fOff].id] = coerceForField(fields[fOff], val); flashKeys.push(`${targetRi},${fOff}`); }
                  });
                });
                return next;
              });
              flash(flashKeys);
              setSel({ ri: tRi, fi: 0 });
              setSelEnd({ ri: riAt(lastPos), fi: Math.min(tsvRows[0].length - 1, LAST_FI) });
              if (dropped) {
                setSaveMsg(`Pasted, but ${dropped} row${dropped === 1 ? '' : 's'} had nowhere to go — clear the filter or sort first.`);
                setTimeout(() => setSaveMsg(null), 6000);
              }
              scheduleAutosave(true);
              setCtxMenu(null);
            }, separator: true },
            // "Above" and "below" are meaningless while a sort is reordering the display: the new
            // row would appear wherever its blank values sort to, not where it was asked for. Under
            // a filter it is worse — a blank row matches nothing, so it is created and immediately
            // hidden, which reads as the menu item doing nothing at all.
            { label: canGrow ? 'Insert row above' : 'Insert row above (clear filter/sort first)', icon: '↑', disabled: !canGrow,
              action: () => { if (!canGrow) return; insertRowAbove(ctxMenu.ri); setCtxMenu(null); }},
            { label: canGrow ? 'Insert row below' : 'Insert row below (clear filter/sort first)', icon: '↓', separator: true, disabled: !canGrow,
              action: () => { if (!canGrow) return; insertRowBelow(ctxMenu.ri); setCtxMenu(null); }},
            {
              label: selRows.size > 1 && selRows.has(ctxMenu.ri) ? `Delete ${selRows.size} rows` : 'Delete row',
              icon: '✕', danger: true,
              action: () => {
                const toDelete = selRows.size > 1 && selRows.has(ctxMenu.ri) ? selRows : new Set([ctxMenu.ri]);
                setRows(prev => { pushUndo(prev); return prev.filter((_, i) => !toDelete.has(i)); });
                setSelRows(new Set()); setSel(null); setSelEnd(null); setEditCell(null);
                lastRowAnchor.current = null; setCtxMenu(null);
                scheduleAutosave(true);
              },
            },
          ].map(({ label, icon, action, separator, danger, disabled }) => (
            <button key={label} onClick={action} disabled={disabled} style={{
              display: 'flex', alignItems: 'center', gap: 10, width: '100%',
              padding: '9px 16px', background: 'transparent', border: 'none',
              borderTop: separator ? `1px solid ${C.border}` : 'none',
              color: danger ? C.danger : C.text, fontSize: 13, textAlign: 'left',
              cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.45 : 1, fontFamily: 'inherit',
            }}
              onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = C.s2; }}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <span style={{ color: danger ? C.danger : C.muted, fontSize: 14, width: 16, textAlign: 'center' }}>{icon}</span>
              {label}
            </button>
          ))}
        </div>
      )}

      {showImport && (
        <ImportModal
          fields={fields}
          dictValues={dictValues}
          optionsByField={optionsByField}
          onClose={() => setShowImport(false)}
          onImport={(valueRows, mode) => {
            const newRows = valueRows.map(values => ({ id: adapter ? adapter.createRowId() : uid(), values }));
            setRows(prev => {
              pushUndo(prev);
              // Appending onto a sheet of untouched blank rows would leave them stranded above
              // the imported data, so the seeded blanks give way.
              const keep = mode === 'replace' ? [] : prev.filter(rowHasContent);
              return [...keep, ...newRows];
            });
            setActiveFilters({}); setSortSpec([]);
            setSaveMsg(`Imported ${newRows.length} row${newRows.length !== 1 ? 's' : ''}`);
            setTimeout(() => setSaveMsg(null), 3000);
            scheduleAutosave(true);
          }}
        />
      )}

      {allowBatchGenerate && showBatch && (
        <BatchGenerateModal
          taxonomy={taxonomy}
          dictValues={dictValues}
          preview={adapter?.batchPreview}
          onGenerate={newRows => {
            const added = adapter?.createRowId ? newRows.map(row => ({ ...row, id: adapter.createRowId() })) : newRows;
            setRows(prev => { pushUndo(prev); return [...prev, ...added]; });
            scheduleAutosave(true);
          }}
          onClose={() => setShowBatch(false)}
        />
      )}
    </div>
  );
}
